import { ExtendedCourse } from '@/types/courseTypes';

export const SERVERLESS_EDGE_COURSES: ExtendedCourse[] = [
  {
    id: 'aws-lambda-development',
    title: 'AWS Lambda Development',
    description: 'Master serverless development with AWS Lambda, from basic functions to advanced architectures.',
    level: 'intermediate',
    totalDuration: '480 minutes',
    image: '/placeholder.svg',
    prerequisites: ['Basic programming knowledge', 'Understanding of HTTP/REST APIs', 'AWS account'],
    learningOutcomes: ['Build serverless applications', 'Deploy Lambda functions', 'Implement event-driven architectures'],
    type: 'serverless-edge',
    courseType: 'serverless-edge',
    modules: [
      {
        id: 'lambda-basics',
        title: 'Lambda Fundamentals',
        description: 'Introduction to AWS Lambda and serverless computing concepts.',
        order: 1,
        estimatedTime: '120 minutes',
        duration: '120 minutes',
        difficulty: 'beginner',
        content: `# Lambda Fundamentals

## What is AWS Lambda?

AWS Lambda is a serverless computing service that lets you run code without provisioning or managing servers. You pay only for the compute time you consume.

### Key Benefits:
- **No server management** - AWS handles infrastructure
- **Automatic scaling** - Scales with your application traffic
- **Pay-per-use** - Only pay for requests and execution time
- **Built-in fault tolerance** - Automatic failover and redundancy

## Lambda Function Structure

Every Lambda function has three main components:

1. **Handler** - Entry point for your function
2. **Event** - Input data from trigger
3. **Context** - Runtime information

\\\`\\\`\\\`javascript
// Basic Lambda function structure
exports.handler = async (event, context) => {
    // Your function logic here
    console.log('Event:', JSON.stringify(event, null, 2));
    console.log('Context:', JSON.stringify(context, null, 2));
    
    return {
        statusCode: 200,
        body: JSON.stringify({
            message: 'Hello from Lambda!',
            requestId: context.awsRequestId
        })
    };
};
\\\`\\\`\\\`

## Supported Runtimes

Lambda supports multiple programming languages:
- **Node.js** (JavaScript/TypeScript)
- **Python**
- **Java**
- **Go**
- **C# (.NET)**
- **Ruby**
- **Custom runtimes**

## Execution Environment

Lambda functions run in a secure, isolated execution environment that includes:
- Amazon Linux-based environment
- AWS SDK pre-installed
- /tmp directory (512 MB storage)
- Environment variables
- Execution role for AWS permissions

## Memory and Timeout Configuration

- **Memory**: 128 MB to 10,240 MB
- **Timeout**: 1 second to 15 minutes
- **Ephemeral storage**: 512 MB to 10,240 MB

Higher memory allocation also increases CPU power proportionally.`,
        objectives: [
          'Understand serverless computing concepts',
          'Learn Lambda function structure',
          'Explore execution environment',
          'Configure memory and timeout settings'
        ],
        codeExamples: [
          {
            language: 'javascript',
            title: 'Simple Hello World Lambda',
            code: `exports.handler = async (event) => {
    return {
        statusCode: 200,
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            message: 'Hello, World!',
            timestamp: new Date().toISOString()
        }),
    };
};`,
            explanation: 'A basic Lambda function that returns a JSON response'
          }
        ],
        quiz: {
          questions: [
            {
              id: 'lambda-max-execution-time',
              question: 'What is the maximum execution time for a Lambda function?',
              options: ['5 minutes', '10 minutes', '15 minutes', '30 minutes'],
              correctAnswer: 2,
              explanation: 'Lambda functions can run for a maximum of 15 minutes.'
            }
          ]
        }
      },
      {
        id: 'event-triggers',
        title: 'Event Sources & Triggers',
        description: 'Learn about different event sources that can trigger Lambda functions.',
        order: 2,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'beginner',
        content: `# Event Sources & Triggers

Lambda functions are event-driven, meaning they execute in response to events from various AWS services.

## Common Event Sources

### 1. API Gateway
HTTP requests trigger Lambda functions through REST APIs.

\\\`\\\`\\\`javascript
// API Gateway event structure
exports.handler = async (event) => {
    const { httpMethod, path, queryStringParameters, body } = event;
    
    if (httpMethod === 'GET') {
        return {
            statusCode: 200,
            body: JSON.stringify({ message: 'GET request received' })
        };
    }
    
    if (httpMethod === 'POST') {
        const data = JSON.parse(body);
        return {
            statusCode: 201,
            body: JSON.stringify({ 
                message: 'Data created', 
                data 
            })
        };
    }
};
\\\`\\\`\\\`

### 2. S3 Events
S3 bucket events (object creation, deletion) can trigger Lambda.

\\\`\\\`\\\`javascript
// S3 event processing
exports.handler = async (event) => {
    const records = event.Records;
    
    for (const record of records) {
        const bucket = record.s3.bucket.name;
        const key = record.s3.object.key;
        const eventName = record.eventName;
        
        console.log(\\\`\\\${eventName} event for \\\${key} in \\\${bucket}\\\`);
        
        if (eventName.startsWith('ObjectCreated')) {
            // Process new object
            await processNewObject(bucket, key);
        }
    }
};
\\\`\\\`\\\`

### 3. DynamoDB Streams
DynamoDB table changes trigger Lambda functions.

\\\`\\\`\\\`javascript
// DynamoDB stream processing
exports.handler = async (event) => {
    for (const record of event.Records) {
        const { eventName, dynamodb } = record;
        
        switch (eventName) {
            case 'INSERT':
                console.log('New item:', dynamodb.NewImage);
                break;
            case 'MODIFY':
                console.log('Updated item:', dynamodb.NewImage);
                console.log('Old values:', dynamodb.OldImage);
                break;
            case 'REMOVE':
                console.log('Deleted item:', dynamodb.OldImage);
                break;
        }
    }
};
\\\`\\\`\\\`

## Event Processing Patterns

### Synchronous Processing
- API Gateway
- Application Load Balancer
- Lambda function URLs

### Asynchronous Processing
- S3 events
- SNS notifications
- CloudWatch Events

### Stream Processing
- DynamoDB Streams
- Kinesis Data Streams
- MSK (Kafka)

## Error Handling

Different event sources handle errors differently:

\\\`\\\`\\\`javascript
exports.handler = async (event) => {
    try {
        // Process event
        const result = await processEvent(event);
        return result;
    } catch (error) {
        console.error('Error processing event:', error);
        
        // For async sources, throw error to trigger retry
        if (event.Records) {
            throw error;
        }
        
        // For sync sources, return error response
        return {
            statusCode: 500,
            body: JSON.stringify({ 
                error: 'Internal server error' 
            })
        };
    }
};
\\\`\\\`\\\``,
        objectives: [
          'Understand different event sources',
          'Process API Gateway events',
          'Handle S3 and DynamoDB events',
          'Implement error handling strategies'
        ]
      },
      {
        id: 'lambda-deployment',
        title: 'Deployment & Packaging',
        description: 'Learn how to package and deploy Lambda functions effectively.',
        order: 3,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'intermediate',
        content: `# Deployment & Packaging

## Deployment Packages

Lambda functions can be deployed in several ways:

### 1. Zip File Upload
For small functions (< 50 MB):

\\\`\\\`\\\`bash
# Create deployment package
zip function.zip index.js package.json node_modules/

# Upload via AWS CLI
aws lambda create-function \\
  --function-name my-function \\
  --runtime nodejs18.x \\
  --role arn:aws:iam::account:role/lambda-role \\
  --handler index.handler \\
  --zip-file fileb://function.zip
\\\`\\\`\\\`

### 2. Container Images
For larger applications or custom runtimes:

\\\`\\\`\\\`dockerfile
# Dockerfile for Lambda
FROM public.ecr.aws/lambda/nodejs:18

# Copy function code
COPY index.js package*.json ./

# Install dependencies
RUN npm ci --only=production

# Set the CMD to your handler
CMD [ "index.handler" ]
\\\`\\\`\\\`

### 3. AWS SAM (Serverless Application Model)

\\\`\\\`\\\`yaml
# template.yaml
AWSTemplateFormatVersion: '2010-09-09'
Transform: AWS::Serverless-2016-10-31

Resources:
  MyFunction:
    Type: AWS::Serverless::Function
    Properties:
      CodeUri: src/
      Handler: index.handler
      Runtime: nodejs18.x
      Events:
        Api:
          Type: Api
          Properties:
            Path: /hello
            Method: get
\\\`\\\`\\\`

\\\`\\\`\\\`bash
# Deploy with SAM
sam build
sam deploy --guided
\\\`\\\`\\\`

## Environment Variables

\\\`\\\`\\\`javascript
// Using environment variables
const dbHost = process.env.DB_HOST;
const apiKey = process.env.API_KEY;

exports.handler = async (event) => {
    const connection = await connectToDatabase(dbHost);
    // Function logic
};
\\\`\\\`\\\`

## Layers

Lambda Layers allow you to share code across multiple functions:

\\\`\\\`\\\`yaml
# SAM template with layer
Resources:
  UtilsLayer:
    Type: AWS::Serverless::LayerVersion
    Properties:
      LayerName: utils-layer
      Description: Shared utilities
      ContentUri: layers/utils/
      CompatibleRuntimes:
        - nodejs18.x

  MyFunction:
    Type: AWS::Serverless::Function
    Properties:
      Layers:
        - !Ref UtilsLayer
\\\`\\\`\\\`

## Versioning and Aliases

\\\`\\\`\\\`bash
# Publish a version
aws lambda publish-version --function-name my-function

# Create an alias
aws lambda create-alias \\
  --function-name my-function \\
  --name PROD \\
  --function-version 1
\\\`\\\`\\\`

## CI/CD Pipeline Example

\\\`\\\`\\\`yaml
# GitHub Actions workflow
name: Deploy Lambda
on:
  push:
    branches: [main]

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Setup Node.js
        uses: actions/setup-node@v2
        with:
          node-version: '18'
      
      - name: Install dependencies
        run: npm ci
      
      - name: Run tests
        run: npm test
      
      - name: Configure AWS credentials
        uses: aws-actions/configure-aws-credentials@v1
        with:
          aws-access-key-id: \\\${{ secrets.AWS_ACCESS_KEY_ID }}
          aws-secret-access-key: \\\${{ secrets.AWS_SECRET_ACCESS_KEY }}
          aws-region: us-east-1
      
      - name: Deploy with SAM
        run: |
          sam build
          sam deploy --no-confirm-changeset
\\\`\\\`\\\``,
        objectives: [
          'Package Lambda functions for deployment',
          'Use AWS SAM for infrastructure as code',
          'Implement versioning and aliases',
          'Set up CI/CD pipelines'
        ]
      },
      {
        id: 'lambda-optimization',
        title: 'Performance Optimization',
        description: 'Optimize Lambda functions for cost and performance.',
        order: 4,
        estimatedTime: '120 minutes',
        duration: '120 minutes',
        difficulty: 'advanced',
        content: `# Performance Optimization

## Cold Start Optimization

Cold starts occur when Lambda creates a new execution environment.

### Minimize Package Size

\\\`\\\`\\\`javascript
// Import only what you need
const { DynamoDBClient, GetItemCommand } = require('@aws-sdk/client-dynamodb');

// Instead of importing entire SDK
// const AWS = require('aws-sdk');
\\\`\\\`\\\`

### Connection Reuse

\\\`\\\`\\\`javascript
// Initialize connections outside handler
const dynamoClient = new DynamoDBClient({ region: 'us-east-1' });

exports.handler = async (event) => {
    // Reuse connection across invocations
    const command = new GetItemCommand({
        TableName: 'Users',
        Key: { id: { S: event.pathParameters.id } }
    });
    
    const response = await dynamoClient.send(command);
    return {
        statusCode: 200,
        body: JSON.stringify(response.Item)
    };
};
\\\`\\\`\\\`

### Provisioned Concurrency

\\\`\\\`\\\`yaml
# SAM template with provisioned concurrency
Resources:
  MyFunction:
    Type: AWS::Serverless::Function
    Properties:
      ProvisionedConcurrencyConfig:
        ProvisionedConcurrencyMetric: 100
\\\`\\\`\\\`

## Memory and CPU Optimization

Memory allocation affects CPU power:

\\\`\\\`\\\`javascript
// CPU-intensive function
exports.handler = async (event) => {
    const startTime = Date.now();
    
    // Perform computation
    const result = performHeavyComputation(event.data);
    
    const duration = Date.now() - startTime;
    console.log(\\\`Execution time: \\\${duration}ms\\\`);
    
    return {
        statusCode: 200,
        body: JSON.stringify({ result, duration })
    };
};
\\\`\\\`\\\`

## Monitoring and Metrics

### CloudWatch Metrics

\\\`\\\`\\\`javascript
const { CloudWatchClient, PutMetricDataCommand } = require('@aws-sdk/client-cloudwatch');

const cloudwatch = new CloudWatchClient({ region: 'us-east-1' });

const putCustomMetric = async (metricName, value) => {
    const command = new PutMetricDataCommand({
        Namespace: 'MyApp/Lambda',
        MetricData: [{
            MetricName: metricName,
            Value: value,
            Unit: 'Count',
            Timestamp: new Date()
        }]
    });
    
    await cloudwatch.send(command);
};

exports.handler = async (event) => {
    try {
        const result = await processRequest(event);
        await putCustomMetric('SuccessfulRequests', 1);
        return result;
    } catch (error) {
        await putCustomMetric('FailedRequests', 1);
        throw error;
    }
};
\\\`\\\`\\\`

### X-Ray Tracing

\\\`\\\`\\\`javascript
const AWSXRay = require('aws-xray-sdk-core');
const AWS = AWSXRay.captureAWS(require('aws-sdk'));

exports.handler = async (event) => {
    const segment = AWSXRay.getSegment();
    const subsegment = segment.addNewSubsegment('database-query');
    
    try {
        const result = await performDatabaseQuery();
        subsegment.addAnnotation('queryResult', 'success');
        return result;
    } catch (error) {
        subsegment.addAnnotation('queryResult', 'error');
        throw error;
    } finally {
        subsegment.close();
    }
};
\\\`\\\`\\\`

## Error Handling and Retries

\\\`\\\`\\\`javascript
const retry = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await fn();
        } catch (error) {
            if (i === maxRetries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
        }
    }
};

exports.handler = async (event) => {
    try {
        const result = await retry(async () => {
            return await externalAPICall(event.data);
        });
        
        return {
            statusCode: 200,
            body: JSON.stringify(result)
        };
    } catch (error) {
        console.error('Failed after retries:', error);
        return {
            statusCode: 500,
            body: JSON.stringify({ error: 'Service unavailable' })
        };
    }
};
\\\`\\\`\\\`

## Cost Optimization

### Right-sizing Memory

\\\`\\\`\\\`bash
# Use AWS Lambda Power Tuning tool
npm install -g aws-lambda-power-tuning

# Run power tuning analysis
aws-lambda-power-tuning \\
  --function-name my-function \\
  --payload '{"test": "data"}' \\
  --duration 30
\\\`\\\`\\\`

### Dead Letter Queues

\\\`\\\`\\\`yaml
# SAM template with DLQ
Resources:
  MyFunction:
    Type: AWS::Serverless::Function
    Properties:
      DeadLetterQueue:
        Type: SQS
        TargetArn: !GetAtt MyDLQ.Arn
        
  MyDLQ:
    Type: AWS::SQS::Queue
\\\`\\\`\\\``,
        objectives: [
          'Optimize cold start performance',
          'Configure memory and concurrency',
          'Implement monitoring and tracing',
          'Handle errors and implement retries'
        ]
      }
    ]
  },
  {
    id: 'edge-computing-fundamentals',
    title: 'Edge Computing Fundamentals',
    description: 'Learn edge computing concepts and implement edge functions with modern platforms.',
    level: 'intermediate',
    totalDuration: '420 minutes',
    image: '/placeholder.svg',
    prerequisites: ['JavaScript knowledge', 'Understanding of web architecture', 'Basic networking concepts'],
    learningOutcomes: ['Understand edge computing principles', 'Build edge functions', 'Optimize edge performance'],
    type: 'serverless-edge',
    courseType: 'serverless-edge',
    modules: [
      {
        id: 'edge-computing-concepts',
        title: 'Edge Computing Concepts',
        description: 'Understanding edge computing principles and benefits.',
        order: 1,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'beginner',
        objectives: ['Understand edge computing principles', 'Learn about CDN and global distribution', 'Explore edge computing benefits'],
        content: `# Edge Computing Concepts

## What is Edge Computing?

Edge computing brings computation and data storage closer to users by processing data at edge locations distributed globally, rather than in centralized data centers.

### Key Benefits

1. **Reduced Latency** - Processing closer to users
2. **Improved Performance** - Faster response times
3. **Better User Experience** - Consistent global performance
4. **Reduced Bandwidth** - Less data transfer to origin
5. **Enhanced Security** - Distributed attack surface

## Edge vs Traditional Computing

### Traditional Architecture
\\\`\\\`\\\`
User → Internet → Load Balancer → Server → Database
     ←                          ←        ←
\\\`\\\`\\\`

### Edge Architecture
\\\`\\\`\\\`
User → Edge Location → Origin (if needed)
     ←               ←
\\\`\\\`\\\`

## Types of Edge Computing

### 1. CDN Edge Computing
- Static content delivery
- Basic request/response manipulation
- Geographic distribution

### 2. Edge Functions
- Dynamic content generation
- API processing at the edge
- Serverless compute at edge locations

### 3. IoT Edge Computing
- Device-level processing
- Real-time data analysis
- Reduced cloud dependency

## Edge Computing Platforms

### Cloudflare Workers
- V8 JavaScript runtime
- Global network (200+ locations)
- KV storage for edge data

### Vercel Edge Functions
- Built on Web APIs
- Integrated with deployment
- Middleware capabilities

### AWS Lambda@Edge
- Runs on CloudFront
- Node.js and Python support
- Integration with AWS services

### Fastly Compute@Edge
- WebAssembly runtime
- Multiple language support
- Real-time analytics

## Use Cases

### 1. API Gateway at Edge
\\\`\\\`\\\`javascript
// Edge function as API gateway
export default async function handler(request) {
  const url = new URL(request.url);
  
  // Route based on path
  if (url.pathname.startsWith('/api/users')) {
    return handleUsers(request);
  } else if (url.pathname.startsWith('/api/orders')) {
    return handleOrders(request);
  }
  
  return new Response('Not Found', { status: 404 });
}
\\\`\\\`\\\`

### 2. Request/Response Transformation
\\\`\\\`\\\`javascript
// Transform responses at edge
export default async function handler(request) {
  const response = await fetch(request);
  const data = await response.json();
  
  // Add user-specific data
  const userCountry = request.cf.country;
  data.localization = {
    country: userCountry,
    currency: getCurrencyForCountry(userCountry)
  };
  
  return new Response(JSON.stringify(data), {
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'public, max-age=300'
    }
  });
}
\\\`\\\`\\\`

### 3. A/B Testing
\\\`\\\`\\\`javascript
// A/B testing at edge
export default async function handler(request) {
  const userId = request.headers.get('user-id');
  const testGroup = hashUserId(userId) % 2 === 0 ? 'A' : 'B';
  
  // Modify request based on test group
  const url = new URL(request.url);
  url.searchParams.set('version', testGroup);
  
  const response = await fetch(url.toString());
  
  // Add test group to response headers
  const newResponse = new Response(response.body, response);
  newResponse.headers.set('X-Test-Group', testGroup);
  
  return newResponse;
}
\\\`\\\`\\\`

## Edge Storage Solutions

### Key-Value Stores
- Cloudflare KV
- Vercel Edge Config
- Fast read access globally

### Edge Databases
- PlanetScale edge caching
- FaunaDB global distribution
- Consistent data access

## Performance Considerations

### Cold Starts
- Edge functions have minimal cold start times
- V8 isolates start faster than containers
- WebAssembly provides near-native performance

### Memory Limits
- Typically 128MB - 512MB
- Optimized for lightweight operations
- Efficient code is crucial

### Execution Time
- Usually 10-50 seconds maximum
- Designed for quick operations
- Long-running tasks need different solutions

## Security at the Edge

### DDoS Protection
\\\`\\\`\\\`javascript
// Rate limiting at edge
const RATE_LIMIT = 100; // requests per minute

export default async function handler(request) {
  const clientIP = request.headers.get('CF-Connecting-IP');
  const key = \\\`rate_limit:\\\${clientIP}\\\`;
  
  // Check current rate
  const current = await EDGE_KV.get(key);
  if (current && parseInt(current) > RATE_LIMIT) {
    return new Response('Rate limit exceeded', { status: 429 });
  }
  
  // Increment counter
  await EDGE_KV.put(key, (parseInt(current) || 0) + 1, { expirationTtl: 60 });
  
  return fetch(request);
}
\\\`\\\`\\\`

### Bot Detection
\\\`\\\`\\\`javascript
// Simple bot detection
export default async function handler(request) {
  const userAgent = request.headers.get('User-Agent');
  const isSuspicious = checkForBotPatterns(userAgent);
  
  if (isSuspicious) {
    // Serve CAPTCHA or block
    return new Response('Please verify you are human', { 
      status: 403,
      headers: { 'Content-Type': 'text/html' }
    });
  }
  
  return fetch(request);
}
\\\`\\\`\\\`

This foundation in edge computing concepts prepares you for implementing specific edge function platforms and optimization strategies.`
      },
      {
        id: 'cloudflare-workers',
        title: 'Cloudflare Workers Development',
        description: 'Build and deploy serverless functions on Cloudflare Workers.',
        order: 2,
        estimatedTime: '120 minutes',
        duration: '120 minutes',
        difficulty: 'intermediate',
        objectives: ['Build Cloudflare Workers', 'Handle HTTP requests', 'Deploy edge functions'],
        content: `# Cloudflare Workers Development

## Introduction to Cloudflare Workers

Cloudflare Workers run on Cloudflare's global network of 200+ data centers, providing serverless computing at the edge with incredible performance and global distribution.

### Key Features
- **V8 JavaScript Engine** - Same engine as Chrome
- **No Cold Starts** - Isolates start in under 5ms
- **Global Network** - Deploy to 200+ locations instantly
- **Web Standards** - Built on standard Web APIs

## Getting Started

### Installation
\\\`\\\`\\\`bash
# Install Wrangler CLI
npm install -g wrangler

# Login to Cloudflare
wrangler login

# Create new project
wrangler generate my-worker
cd my-worker
\\\`\\\`\\\`

### Basic Worker Structure
\\\`\\\`\\\`javascript
// src/index.js
export default {
  async fetch(request, env, ctx) {
    return new Response('Hello World from Cloudflare Workers!');
  },
};
\\\`\\\`\\\`

### Configuration
\\\`\\\`\\\`toml
# wrangler.toml
name = "my-worker"
main = "src/index.js"
compatibility_date = "2023-10-01"

[env.production]
name = "my-worker-prod"
vars = { ENVIRONMENT = "production" }

[env.staging]
name = "my-worker-staging"
vars = { ENVIRONMENT = "staging" }
\\\`\\\`\\\`

## Request Handling

### Basic Request Processing
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    const url = new URL(request.url);
    const method = request.method;
    
    // Handle different HTTP methods
    switch (method) {
      case 'GET':
        return handleGet(url);
      case 'POST':
        return handlePost(request);
      case 'PUT':
        return handlePut(request);
      default:
        return new Response('Method not allowed', { status: 405 });
    }
  },
};

async function handleGet(url) {
  const pathname = url.pathname;
  
  if (pathname === '/') {
    return new Response('Welcome to my API');
  } else if (pathname === '/health') {
    return new Response(JSON.stringify({ status: 'healthy' }), {
      headers: { 'Content-Type': 'application/json' }
    });
  }
  
  return new Response('Not found', { status: 404 });
}
\\\`\\\`\\\`

### Request Headers and Geolocation
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    // Access Cloudflare-specific data
    const country = request.cf?.country || 'Unknown';
    const city = request.cf?.city || 'Unknown';
    const clientIP = request.headers.get('CF-Connecting-IP');
    
    return new Response(JSON.stringify({
      message: 'Hello from your location!',
      country,
      city,
      ip: clientIP,
      userAgent: request.headers.get('User-Agent')
    }), {
      headers: { 'Content-Type': 'application/json' }
    });
  },
};
\\\`\\\`\\\`

## Working with KV Storage

### Setting up KV
\\\`\\\`\\\`bash
# Create KV namespace
wrangler kv:namespace create "MY_KV"

# Add to wrangler.toml
kv_namespaces = [
  { binding = "MY_KV", id = "your-namespace-id" }
]
\\\`\\\`\\\`

### KV Operations
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    const url = new URL(request.url);
    const key = url.searchParams.get('key');
    
    if (request.method === 'GET' && key) {
      // Read from KV
      const value = await env.MY_KV.get(key);
      if (value === null) {
        return new Response('Key not found', { status: 404 });
      }
      return new Response(value);
    }
    
    if (request.method === 'POST' && key) {
      // Write to KV
      const value = await request.text();
      await env.MY_KV.put(key, value, {
        expirationTtl: 3600 // 1 hour
      });
      return new Response('Stored successfully');
    }
    
    return new Response('Invalid request', { status: 400 });
  },
};
\\\`\\\`\\\`

## Durable Objects

### Creating a Durable Object
\\\`\\\`\\\`javascript
// src/counter.js
export class Counter {
  constructor(state, env) {
    this.state = state;
  }
  
  async fetch(request) {
    const url = new URL(request.url);
    
    if (url.pathname === '/increment') {
      let count = await this.state.storage.get('count') || 0;
      count++;
      await this.state.storage.put('count', count);
      return new Response(count.toString());
    }
    
    if (url.pathname === '/get') {
      const count = await this.state.storage.get('count') || 0;
      return new Response(count.toString());
    }
    
    return new Response('Not found', { status: 404 });
  }
}
\\\`\\\`\\\`

### Using Durable Objects
\\\`\\\`\\\`javascript
// src/index.js
import { Counter } from './counter.js';

export { Counter };

export default {
  async fetch(request, env, ctx) {
    const url = new URL(request.url);
    
    if (url.pathname.startsWith('/counter')) {
      // Create or get Durable Object instance
      const id = env.COUNTER.idFromName('global-counter');
      const counter = env.COUNTER.get(id);
      
      // Forward request to Durable Object
      return counter.fetch(request);
    }
    
    return new Response('Not found', { status: 404 });
  },
};
\\\`\\\`\\\`

## Advanced Features

### Streaming Responses
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    const stream = new ReadableStream({
      start(controller) {
        const encoder = new TextEncoder();
        
        // Send initial data
        controller.enqueue(encoder.encode('Starting stream...\\n'));
        
        // Simulate streaming data
        let count = 0;
        const interval = setInterval(() => {
          controller.enqueue(
            encoder.encode(\\\`Data chunk \\\${++count}\\n\\\`)
          );
          
          if (count >= 10) {
            clearInterval(interval);
            controller.close();
          }
        }, 1000);
      }
    });
    
    return new Response(stream, {
      headers: {
        'Content-Type': 'text/plain',
        'Transfer-Encoding': 'chunked'
      }
    });
  },
};
\\\`\\\`\\\`

### WebSocket Connections
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    if (request.headers.get('Upgrade') === 'websocket') {
      return handleWebSocket(request);
    }
    
    return new Response('Expected WebSocket', { status: 426 });
  },
};

async function handleWebSocket(request) {
  const webSocketPair = new WebSocketPair();
  const [client, server] = Object.values(webSocketPair);
  
  server.accept();
  
  server.addEventListener('message', event => {
    // Echo messages back
    server.send(\\\`Echo: \\\${event.data}\\\`);
  });
  
  return new Response(null, {
    status: 101,
    webSocket: client,
  });
}
\\\`\\\`\\\`

## Deployment and Testing

### Local Development
\\\`\\\`\\\`bash
# Start local development server
wrangler dev

# Test with specific environment
wrangler dev --env staging
\\\`\\\`\\\`

### Deployment
\\\`\\\`\\\`bash
# Deploy to production
wrangler publish

# Deploy to specific environment
wrangler publish --env production
\\\`\\\`\\\`

### Testing
\\\`\\\`\\\`javascript
// tests/index.test.js
import { unstable_dev } from 'wrangler';

describe('Worker', () => {
  let worker;
  
  beforeAll(async () => {
    worker = await unstable_dev('src/index.js', {
      experimental: { disableExperimentalWarning: true },
    });
  });
  
  afterAll(async () => {
    await worker.stop();
  });
  
  it('should return hello world', async () => {
    const resp = await worker.fetch();
    expect(resp.status).toBe(200);
    
    const text = await resp.text();
    expect(text).toBe('Hello World from Cloudflare Workers!');
  });
});
\\\`\\\`\\\`

## Performance Optimization

### Caching Strategies
\\\`\\\`\\\`javascript
export default {
  async fetch(request, env, ctx) {
    const cache = caches.default;
    const cacheKey = new Request(request.url, request);
    
    // Check cache first
    let response = await cache.match(cacheKey);
    
    if (!response) {
      // Generate response
      response = await generateResponse(request);
      
      // Cache for 1 hour
      response.headers.set('Cache-Control', 'public, max-age=3600');
      
      // Store in cache
      ctx.waitUntil(cache.put(cacheKey, response.clone()));
    }
    
    return response;
  },
};
\\\`\\\`\\\`

This comprehensive guide provides everything needed to build production-ready applications with Cloudflare Workers.`
      },
      {
        id: 'vercel-edge-functions',
        title: 'Vercel Edge Functions',
        description: 'Implement edge computing with Vercel Edge Functions and middleware.',
        order: 3,
        estimatedTime: '105 minutes',
        duration: '105 minutes',
        difficulty: 'intermediate',
        objectives: ['Implement Vercel Edge Functions', 'Create middleware', 'Deploy edge applications'],
        content: `# Vercel Edge Functions

## Introduction to Vercel Edge Functions

Vercel Edge Functions run on the Vercel Edge Network, providing serverless compute at the edge with seamless integration into your deployment workflow.

### Key Features
- **Global Distribution** - Runs on Vercel's global edge network
- **Web API Standards** - Built on standard Web APIs
- **Zero Configuration** - Automatic deployment with your app
- **TypeScript Support** - First-class TypeScript support

## Getting Started

### Project Setup
\\\`\\\`\\\`bash
# Create new Next.js project with Vercel
npx create-next-app@latest my-edge-app
cd my-edge-app

# Or add to existing project
npm install next react react-dom
\\\`\\\`\\\`

### Basic Edge Function
\\\`\\\`\\\`javascript
// api/edge/hello.js
export const config = {
  runtime: 'edge',
};

export default function handler(request) {
  return new Response('Hello from Vercel Edge Functions!', {
    status: 200,
    headers: {
      'Content-Type': 'text/plain',
    },
  });
}
\\\`\\\`\\\`

### TypeScript Support
\\\`\\\`\\\`typescript
// api/edge/hello.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default function handler(req: NextRequest) {
  return NextResponse.json({
    message: 'Hello from TypeScript Edge Function!',
    timestamp: new Date().toISOString(),
  });
}
\\\`\\\`\\\`

## Request Handling

### Processing Different HTTP Methods
\\\`\\\`\\\`typescript
// api/edge/users.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default async function handler(req: NextRequest) {
  const { method } = req;
  const url = new URL(req.url);
  
  switch (method) {
    case 'GET':
      return handleGet(url);
    case 'POST':
      return handlePost(req);
    case 'PUT':
      return handlePut(req);
    case 'DELETE':
      return handleDelete(url);
    default:
      return new NextResponse('Method not allowed', { status: 405 });
  }
}

async function handleGet(url: URL) {
  const userId = url.searchParams.get('id');
  
  if (userId) {
    // Fetch specific user
    const user = await fetchUser(userId);
    return NextResponse.json(user);
  }
  
  // Fetch all users
  const users = await fetchUsers();
  return NextResponse.json(users);
}

async function handlePost(req: NextRequest) {
  const body = await req.json();
  const newUser = await createUser(body);
  
  return NextResponse.json(newUser, { status: 201 });
}
\\\`\\\`\\\`

### Geolocation and Headers
\\\`\\\`\\\`typescript
// api/edge/location.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default function handler(req: NextRequest) {
  // Access geolocation data
  const country = req.geo?.country || 'Unknown';
  const region = req.geo?.region || 'Unknown';
  const city = req.geo?.city || 'Unknown';
  const latitude = req.geo?.latitude || 'Unknown';
  const longitude = req.geo?.longitude || 'Unknown';
  
  // Access request headers
  const userAgent = req.headers.get('user-agent');
  const acceptLanguage = req.headers.get('accept-language');
  
  return NextResponse.json({
    location: {
      country,
      region,
      city,
      coordinates: { latitude, longitude }
    },
    client: {
      userAgent,
      acceptLanguage,
      ip: req.ip
    }
  });
}
\\\`\\\`\\\`

## Middleware

### Creating Middleware
\\\`\\\`\\\`typescript
// middleware.ts (in project root)
import { NextRequest, NextResponse } from 'next/server';

export function middleware(request: NextRequest) {
  // Run middleware logic
  const response = NextResponse.next();
  
  // Add custom headers
  response.headers.set('X-Custom-Header', 'Edge-Powered');
  response.headers.set('X-Timestamp', new Date().toISOString());
  
  return response;
}

export const config = {
  matcher: [
    /*
     * Match all request paths except for the ones starting with:
     * - api (API routes)
     * - _next/static (static files)
     * - _next/image (image optimization files)
     * - favicon.ico (favicon file)
     */
    '/((?!api|_next/static|_next/image|favicon.ico).*)',
  ],
};
\\\`\\\`\\\`

### Authentication Middleware
\\\`\\\`\\\`typescript
// middleware.ts
import { NextRequest, NextResponse } from 'next/server';
import { verify } from 'jsonwebtoken';

export async function middleware(request: NextRequest) {
  // Check if route requires authentication
  if (request.nextUrl.pathname.startsWith('/protected')) {
    const token = request.cookies.get('auth-token')?.value;
    
    if (!token) {
      return NextResponse.redirect(new URL('/login', request.url));
    }
    
    try {
      // Verify JWT token
      const payload = verify(token, process.env.JWT_SECRET!);
      
      // Add user info to request headers
      const response = NextResponse.next();
      response.headers.set('X-User-ID', payload.sub as string);
      
      return response;
    } catch (error) {
      return NextResponse.redirect(new URL('/login', request.url));
    }
  }
  
  return NextResponse.next();
}
\\\`\\\`\\\`

### A/B Testing Middleware
\\\`\\\`\\\`typescript
// middleware.ts
import { NextRequest, NextResponse } from 'next/server';

export function middleware(request: NextRequest) {
  // A/B test for homepage
  if (request.nextUrl.pathname === '/') {
    const bucket = getBucket(request);
    
    if (bucket === 'B') {
      // Rewrite to variant B
      return NextResponse.rewrite(new URL('/variants/homepage-b', request.url));
    }
  }
  
  return NextResponse.next();
}

function getBucket(request: NextRequest): 'A' | 'B' {
  // Check for existing bucket in cookies
  const existingBucket = request.cookies.get('ab-test-bucket')?.value;
  if (existingBucket === 'A' || existingBucket === 'B') {
    return existingBucket;
  }
  
  // Assign new bucket (50/50 split)
  const bucket = Math.random() < 0.5 ? 'A' : 'B';
  
  // Set cookie for consistent experience
  const response = NextResponse.next();
  response.cookies.set('ab-test-bucket', bucket, {
    maxAge: 60 * 60 * 24 * 30, // 30 days
  });
  
  return bucket;
}
\\\`\\\`\\\`

## Environment Variables and Secrets

### Configuration
\\\`\\\`\\\`bash
# .env.local
EDGE_API_KEY=your-api-key
DATABASE_URL=your-database-url
\\\`\\\`\\\`

### Using Environment Variables
\\\`\\\`\\\`typescript
// api/edge/secure.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default function handler(req: NextRequest) {
  const apiKey = process.env.EDGE_API_KEY;
  const providedKey = req.headers.get('x-api-key');
  
  if (!providedKey || providedKey !== apiKey) {
    return new NextResponse('Unauthorized', { status: 401 });
  }
  
  return NextResponse.json({ message: 'Access granted' });
}
\\\`\\\`\\\`

## Data Fetching

### External API Calls
\\\`\\\`\\\`typescript
// api/edge/proxy.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default async function handler(req: NextRequest) {
  const url = new URL(req.url);
  const endpoint = url.searchParams.get('endpoint');
  
  if (!endpoint) {
    return new NextResponse('Missing endpoint parameter', { status: 400 });
  }
  
  try {
    // Fetch from external API
    const response = await fetch(\\\`https://api.example.com/\\\${endpoint}\\\`, {
      headers: {
        'Authorization': \\\`Bearer \\\${process.env.API_TOKEN}\\\`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(\\\`API error: \\\${response.status}\\\`);
    }
    
    const data = await response.json();
    
    // Add caching headers
    return NextResponse.json(data, {
      headers: {
        'Cache-Control': 'public, s-maxage=60, stale-while-revalidate=300',
      },
    });
  } catch (error) {
    return NextResponse.json(
      { error: 'Failed to fetch data' },
      { status: 500 }
    );
  }
}
\\\`\\\`\\\`

### Database Integration
\\\`\\\`\\\`typescript
// api/edge/database.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

// Use edge-compatible database clients
// Example with PlanetScale serverless driver
import { connect } from '@planetscale/database';

const db = connect({
  url: process.env.DATABASE_URL,
});

export default async function handler(req: NextRequest) {
  try {
    const results = await db.execute('SELECT * FROM users LIMIT 10');
    
    return NextResponse.json({
      users: results.rows,
      count: results.rows.length,
    });
  } catch (error) {
    console.error('Database error:', error);
    return NextResponse.json(
      { error: 'Database query failed' },
      { status: 500 }
    );
  }
}
\\\`\\\`\\\`

## Performance and Monitoring

### Response Optimization
\\\`\\\`\\\`typescript
// api/edge/optimized.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default async function handler(req: NextRequest) {
  const start = Date.now();
  
  // Process request
  const data = await processData(req);
  
  const duration = Date.now() - start;
  
  // Add performance headers
  const response = NextResponse.json(data);
  response.headers.set('X-Response-Time', \\\`\\\${duration}ms\\\`);
  response.headers.set('X-Edge-Location', process.env.VERCEL_REGION || 'unknown');
  
  return response;
}
\\\`\\\`\\\`

### Error Handling
\\\`\\\`\\\`typescript
// api/edge/error-handling.ts
import { NextRequest, NextResponse } from 'next/server';

export const config = {
  runtime: 'edge',
};

export default async function handler(req: NextRequest) {
  try {
    const result = await riskyOperation();
    return NextResponse.json(result);
  } catch (error) {
    console.error('Edge function error:', error);
    
    // Log to external service
    await logError(error, req);
    
    return NextResponse.json(
      { 
        error: 'Internal server error',
        requestId: generateRequestId(),
      },
      { status: 500 }
    );
  }
}

async function logError(error: Error, req: NextRequest) {
  // Example: Send to logging service
  await fetch('https://api.logging-service.com/errors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      error: error.message,
      stack: error.stack,
      url: req.url,
      userAgent: req.headers.get('user-agent'),
      timestamp: new Date().toISOString(),
    }),
  });
}
\\\`\\\`\\\`

## Deployment

### Local Development
\\\`\\\`\\\`bash
# Start development server
npm run dev

# Test edge functions locally
curl http://localhost:3000/api/edge/hello
\\\`\\\`\\\`

### Production Deployment
\\\`\\\`\\\`bash
# Deploy to Vercel
vercel --prod

# Or using GitHub integration
git push origin main
\\\`\\\`\\\`

This comprehensive guide covers all aspects of building and deploying edge functions with Vercel's platform.`
      },
      {
        id: 'edge-performance-optimization',
        title: 'Edge Performance Optimization',
        description: 'Advanced techniques for optimizing edge function performance and user experience.',
        order: 4,
        estimatedTime: '105 minutes',
        duration: '105 minutes',
        difficulty: 'advanced',
        objectives: ['Optimize edge performance', 'Implement caching strategies', 'Monitor edge metrics'],
        content: `# Edge Performance Optimization

## Performance Fundamentals

Edge computing performance optimization focuses on minimizing latency, reducing bandwidth usage, and maximizing cache efficiency while maintaining functionality.

### Key Performance Metrics
- **Time to First Byte (TTFB)** - Initial response time
- **Total Response Time** - Complete request duration
- **Cache Hit Ratio** - Percentage of cached responses
- **Error Rate** - Failed requests percentage
- **Throughput** - Requests per second capacity

## Caching Strategies

### HTTP Caching Headers
\\\`\\\`\\\`javascript
// Optimized caching strategy
export default async function handler(request) {
  const url = new URL(request.url);
  const path = url.pathname;
  
  // Different caching strategies for different content types
  if (path.startsWith('/api/static-data')) {
    return handleStaticData(request, {
      cacheControl: 'public, max-age=3600, s-maxage=86400', // 1 hour browser, 24 hour edge
    });
  } else if (path.startsWith('/api/user-data')) {
    return handleUserData(request, {
      cacheControl: 'private, max-age=300', // 5 minutes private cache
    });
  } else if (path.startsWith('/api/real-time')) {
    return handleRealTimeData(request, {
      cacheControl: 'no-cache, no-store, must-revalidate',
    });
  }
}

async function handleStaticData(request, options) {
  const data = await fetchStaticData();
  
  return new Response(JSON.stringify(data), {
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': options.cacheControl,
      'ETag': generateETag(data),
      'Last-Modified': new Date().toUTCString(),
    },
  });
}
\\\`\\\`\\\`

### Conditional Requests
\\\`\\\`\\\`javascript
// Handle conditional requests for efficiency
export default async function handler(request) {
  const ifNoneMatch = request.headers.get('If-None-Match');
  const ifModifiedSince = request.headers.get('If-Modified-Since');
  
  // Get current data and metadata
  const data = await getCurrentData();
  const currentETag = generateETag(data);
  const lastModified = await getLastModified();
  
  // Check if client has current version
  if (ifNoneMatch === currentETag) {
    return new Response(null, { status: 304 });
  }
  
  if (ifModifiedSince && new Date(ifModifiedSince) >= lastModified) {
    return new Response(null, { status: 304 });
  }
  
  // Return fresh data
  return new Response(JSON.stringify(data), {
    headers: {
      'Content-Type': 'application/json',
      'ETag': currentETag,
      'Last-Modified': lastModified.toUTCString(),
      'Cache-Control': 'public, max-age=3600',
    },
  });
}
\\\`\\\`\\\`

### Stale-While-Revalidate Pattern
\\\`\\\`\\\`javascript
// Implement stale-while-revalidate for optimal UX
export default async function handler(request, env, ctx) {
  const cacheKey = new Request(request.url);
  const cache = caches.default;
  
  // Try to get from cache first
  let response = await cache.match(cacheKey);
  
  if (response) {
    const cacheDate = new Date(response.headers.get('Date'));
    const staleThreshold = 5 * 60 * 1000; // 5 minutes
    
    if (Date.now() - cacheDate.getTime() > staleThreshold) {
      // Serve stale content immediately
      const staleResponse = response.clone();
      
      // Revalidate in background
      ctx.waitUntil(revalidateCache(request, cacheKey, cache));
      
      // Add stale indicator
      staleResponse.headers.set('X-Cache-Status', 'stale');
      return staleResponse;
    }
    
    response.headers.set('X-Cache-Status', 'hit');
    return response;
  }
  
  // Cache miss - fetch fresh data
  response = await fetchFreshData(request);
  
  // Cache the response
  ctx.waitUntil(cache.put(cacheKey, response.clone()));
  
  response.headers.set('X-Cache-Status', 'miss');
  return response;
}

async function revalidateCache(request, cacheKey, cache) {
  try {
    const freshResponse = await fetchFreshData(request);
    await cache.put(cacheKey, freshResponse);
  } catch (error) {
    console.error('Revalidation failed:', error);
  }
}
\\\`\\\`\\\`

## Request Optimization

### Request Deduplication
\\\`\\\`\\\`javascript
// Deduplicate concurrent requests to same resource
const pendingRequests = new Map();

export default async function handler(request) {
  const url = request.url;
  const method = request.method;
  const requestKey = \\\`\\\${method}:\\\${url}\\\`;
  
  // Check if same request is already in progress
  if (pendingRequests.has(requestKey)) {
    return await pendingRequests.get(requestKey);
  }
  
  // Create new request promise
  const requestPromise = processRequest(request);
  pendingRequests.set(requestKey, requestPromise);
  
  try {
    const response = await requestPromise;
    return response;
  } finally {
    // Clean up after request completes
    pendingRequests.delete(requestKey);
  }
}

async function processRequest(request) {
  // Simulate expensive operation
  await new Promise(resolve => setTimeout(resolve, 1000));
  return new Response('Processed successfully');
}
\\\`\\\`\\\`

### Request Batching
\\\`\\\`\\\`javascript
// Batch multiple requests for efficiency
class RequestBatcher {
  constructor(batchSize = 10, maxWaitTime = 100) {
    this.batchSize = batchSize;
    this.maxWaitTime = maxWaitTime;
    this.queue = [];
    this.timer = null;
  }
  
  async addRequest(request) {
    return new Promise((resolve, reject) => {
      this.queue.push({ request, resolve, reject });
      
      if (this.queue.length >= this.batchSize) {
        this.processBatch();
      } else if (!this.timer) {
        this.timer = setTimeout(() => this.processBatch(), this.maxWaitTime);
      }
    });
  }
  
  async processBatch() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    
    const batch = this.queue.splice(0, this.batchSize);
    if (batch.length === 0) return;
    
    try {
      const results = await this.executeBatch(batch.map(item => item.request));
      
      batch.forEach((item, index) => {
        item.resolve(results[index]);
      });
    } catch (error) {
      batch.forEach(item => {
        item.reject(error);
      });
    }
  }
  
  async executeBatch(requests) {
    // Execute all requests concurrently
    return Promise.all(
      requests.map(request => this.executeRequest(request))
    );
  }
  
  async executeRequest(request) {
    // Your request processing logic
    return fetch(request);
  }
}

const batcher = new RequestBatcher();

export default async function handler(request) {
  return await batcher.addRequest(request);
}
\\\`\\\`\\\`

## Data Optimization

### Response Compression
\\\`\\\`\\\`javascript
// Compress responses when beneficial
export default async function handler(request) {
  const acceptEncoding = request.headers.get('Accept-Encoding') || '';
  const supportsGzip = acceptEncoding.includes('gzip');
  const supportsBrotli = acceptEncoding.includes('br');
  
  const data = await generateLargeResponse();
  let responseBody = JSON.stringify(data);
  let headers = {
    'Content-Type': 'application/json',
  };
  
  // Compress large responses
  if (responseBody.length > 1000) {
    if (supportsBrotli) {
      responseBody = await compressBrotli(responseBody);
      headers['Content-Encoding'] = 'br';
    } else if (supportsGzip) {
      responseBody = await compressGzip(responseBody);
      headers['Content-Encoding'] = 'gzip';
    }
  }
  
  headers['Content-Length'] = responseBody.length.toString();
  
  return new Response(responseBody, { headers });
}

// Compression utilities (implementation depends on platform)
async function compressGzip(text) {
  const stream = new CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();
  
  writer.write(new TextEncoder().encode(text));
  writer.close();
  
  const chunks = [];
  let done = false;
  
  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;
    if (value) chunks.push(value);
  }
  
  return new Uint8Array(chunks.reduce((acc, chunk) => [...acc, ...chunk], []));
}
\\\`\\\`\\\`

### Data Filtering and Pagination
\\\`\\\`\\\`javascript
// Optimize data transfer with smart filtering
export default async function handler(request) {
  const url = new URL(request.url);
  const userAgent = request.headers.get('User-Agent');
  
  // Detect device capabilities
  const isMobile = /Mobile|Android|iPhone/i.test(userAgent);
  const isLowBandwidth = request.headers.get('Save-Data') === 'on';
  
  // Adjust response based on client capabilities
  const options = {
    includeImages: !isLowBandwidth,
    imageQuality: isMobile ? 'medium' : 'high',
    pageSize: isLowBandwidth ? 10 : 50,
    includeMetadata: !isMobile,
  };
  
  // Parse query parameters
  const page = parseInt(url.searchParams.get('page') || '1');
  const limit = Math.min(
    parseInt(url.searchParams.get('limit') || options.pageSize.toString()),
    100 // Maximum limit
  );
  const fields = url.searchParams.get('fields')?.split(',');
  
  // Fetch optimized data
  const data = await fetchOptimizedData({
    page,
    limit,
    fields,
    ...options,
  });
  
  const response = new Response(JSON.stringify(data), {
    headers: {
      'Content-Type': 'application/json',
      'X-Device-Type': isMobile ? 'mobile' : 'desktop',
      'X-Bandwidth-Mode': isLowBandwidth ? 'low' : 'normal',
    },
  });
  
  return response;
}
\\\`\\\`\\\`

## Monitoring and Analytics

### Performance Monitoring
\\\`\\\`\\\`javascript
// Built-in performance monitoring
export default async function handler(request, env, ctx) {
  const startTime = Date.now();
  const requestId = generateRequestId();
  
  try {
    // Add request tracking
    const metrics = {
      requestId,
      timestamp: startTime,
      method: request.method,
      url: request.url,
      userAgent: request.headers.get('User-Agent'),
      country: request.cf?.country,
      colo: request.cf?.colo, // Cloudflare data center
    };
    
    const response = await processRequest(request);
    
    // Calculate metrics
    const duration = Date.now() - startTime;
    metrics.duration = duration;
    metrics.status = response.status;
    metrics.responseSize = response.headers.get('Content-Length');
    
    // Log performance data
    ctx.waitUntil(logMetrics(metrics));
    
    // Add performance headers
    response.headers.set('X-Response-Time', \\\`\\\${duration}ms\\\`);
    response.headers.set('X-Request-ID', requestId);
    
    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    
    // Log error metrics
    ctx.waitUntil(logMetrics({
      ...metrics,
      duration,
      error: error.message,
      status: 500,
    }));
    
    throw error;
  }
}

async function logMetrics(metrics) {
  // Send to analytics service
  await fetch('https://analytics.example.com/metrics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(metrics),
  });
}
\\\`\\\`\\\`

### Real-time Performance Dashboard
\\\`\\\`\\\`javascript
// WebSocket-based real-time monitoring
export default async function handler(request) {
  if (request.headers.get('Upgrade') === 'websocket') {
    return handleWebSocketMonitoring(request);
  }
  
  // Regular HTTP request
  return handleHttpRequest(request);
}

async function handleWebSocketMonitoring(request) {
  const webSocketPair = new WebSocketPair();
  const [client, server] = Object.values(webSocketPair);
  
  server.accept();
  
  // Send real-time performance data
  const interval = setInterval(async () => {
    const stats = await getCurrentPerformanceStats();
    server.send(JSON.stringify({
      type: 'performance_update',
      timestamp: Date.now(),
      stats,
    }));
  }, 5000);
  
  server.addEventListener('close', () => {
    clearInterval(interval);
  });
  
  return new Response(null, {
    status: 101,
    webSocket: client,
  });
}

async function getCurrentPerformanceStats() {
  // Gather current performance metrics
  return {
    activeConnections: getActiveConnections(),
    requestsPerSecond: getRequestsPerSecond(),
    averageResponseTime: getAverageResponseTime(),
    errorRate: getErrorRate(),
    cacheHitRatio: getCacheHitRatio(),
  };
}
\\\`\\\`\\\`

## Advanced Optimization Techniques

### Edge-side Includes (ESI)
\\\`\\\`\\\`javascript
// Compose responses from multiple cached fragments
export default async function handler(request) {
  const url = new URL(request.url);
  
  if (url.pathname === '/dashboard') {
    return composeDashboard(request);
  }
  
  return new Response('Not Found', { status: 404 });
}

async function composeDashboard(request) {
  const userId = getUserId(request);
  
  // Fetch different components with different cache strategies
  const [
    userProfile,
    recentActivity,
    notifications,
    staticContent
  ] = await Promise.all([
    fetchCachedComponent('user-profile', userId, { ttl: 300 }), // 5 min
    fetchCachedComponent('recent-activity', userId, { ttl: 60 }), // 1 min
    fetchCachedComponent('notifications', userId, { ttl: 0 }), // No cache
    fetchCachedComponent('static-sidebar', 'global', { ttl: 3600 }), // 1 hour
  ]);
  
  // Compose final HTML
  const html = \\\`
    <!DOCTYPE html>
    <html>
    <body>
      <div id="profile">\\\${userProfile}</div>
      <div id="activity">\\\${recentActivity}</div>
      <div id="notifications">\\\${notifications}</div>
      <div id="sidebar">\\\${staticContent}</div>
    </body>
    </html>
  \\\`;
  
  return new Response(html, {
    headers: {
      'Content-Type': 'text/html',
      'Cache-Control': 'private, max-age=60', // Cache composed page for 1 min
    },
  });
}
\\\`\\\`\\\`

This comprehensive guide provides advanced techniques for maximizing edge function performance and creating optimal user experiences.`
      }
    ]
  },
  {
    id: 'microservices-serverless',
    title: 'Microservices with Serverless',
    description: 'Design and implement microservices architectures using serverless technologies.',
    level: 'advanced',
    totalDuration: '360 minutes',
    image: '/placeholder.svg',
    prerequisites: ['Serverless experience', 'Understanding of distributed systems', 'API design knowledge'],
    learningOutcomes: ['Design microservices architectures', 'Implement event-driven patterns', 'Manage distributed data'],
    type: 'serverless-edge',
    courseType: 'serverless-edge',
    modules: [
      {
        id: 'serverless-microservices-design',
        title: 'Serverless Microservices Design',
        description: 'Design principles and patterns for serverless microservices architecture.',
        order: 1,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'advanced',
        objectives: ['Design microservices architecture', 'Implement serverless patterns', 'Plan service boundaries'],
        content: `# Serverless Microservices Design

## Microservices Architecture Overview

Microservices architecture breaks down applications into small, independent services that communicate over well-defined APIs. When combined with serverless computing, this approach offers unprecedented scalability and cost efficiency.

### Key Principles

1. **Single Responsibility** - Each service has one business capability
2. **Decentralized** - Independent data stores and governance
3. **Fault Isolation** - Failures in one service don't cascade
4. **Technology Diversity** - Choose the right tool for each job
5. **Business-Aligned** - Services align with business domains

## Serverless Microservices Benefits

### Cost Efficiency
- Pay only for actual usage
- No idle server costs
- Automatic scaling to zero

### Operational Simplicity
- No server management
- Built-in high availability
- Automatic scaling

### Development Velocity
- Independent deployment
- Team autonomy
- Faster iteration cycles

## Design Patterns

### 1. API Gateway Pattern
\\\`\\\`\\\`yaml
# API Gateway configuration
Resources:
  ApiGateway:
    Type: AWS::ApiGateway::RestApi
    Properties:
      Name: MicroservicesAPI
      Description: Gateway for microservices
      
  UsersResource:
    Type: AWS::ApiGateway::Resource
    Properties:
      RestApiId: !Ref ApiGateway
      ParentId: !GetAtt ApiGateway.RootResourceId
      PathPart: users
      
  OrdersResource:
    Type: AWS::ApiGateway::Resource
    Properties:
      RestApiId: !Ref ApiGateway
      ParentId: !GetAtt ApiGateway.RootResourceId
      PathPart: orders
\\\`\\\`\\\`

### 2. Service-per-Function Pattern
\\\`\\\`\\\`javascript
// User service functions
// users/create.js
exports.handler = async (event) => {
  const userData = JSON.parse(event.body);
  const user = await createUser(userData);
  
  return {
    statusCode: 201,
    body: JSON.stringify(user)
  };
};

// users/get.js
exports.handler = async (event) => {
  const userId = event.pathParameters.id;
  const user = await getUser(userId);
  
  return {
    statusCode: 200,
    body: JSON.stringify(user)
  };
};

// users/update.js
exports.handler = async (event) => {
  const userId = event.pathParameters.id;
  const updateData = JSON.parse(event.body);
  const user = await updateUser(userId, updateData);
  
  return {
    statusCode: 200,
    body: JSON.stringify(user)
  };
};
\\\`\\\`\\\`

### 3. Aggregate Pattern
\\\`\\\`\\\`javascript
// Order aggregate service
exports.handler = async (event) => {
  const orderId = event.pathParameters.id;
  
  // Fetch data from multiple services in parallel
  const [order, customer, items, shipping] = await Promise.all([
    getOrder(orderId),
    getCustomer(orderCustomerId),
    getOrderItems(orderId),
    getShippingInfo(orderId)
  ]);
  
  // Aggregate response
  const orderAggregate = {
    order: {
      ...order,
      customer,
      items,
      shipping
    }
  };
  
  return {
    statusCode: 200,
    body: JSON.stringify(orderAggregate)
  };
};

async function getOrder(orderId) {
  const response = await fetch(\\\`\\\${ORDER_SERVICE_URL}/orders/\\\${orderId}\\\`);
  return response.json();
}

async function getCustomer(customerId) {
  const response = await fetch(\\\`\\\${USER_SERVICE_URL}/users/\\\${customerId}\\\`);
  return response.json();
}
\\\`\\\`\\\`

## Service Decomposition

### Domain-Driven Design
\\\`\\\`\\\`
Bounded Contexts:
├── User Management
│   ├── Authentication
│   ├── User Profiles
│   └── Permissions
├── Order Management
│   ├── Order Processing
│   ├── Inventory
│   └── Pricing
├── Payment Processing
│   ├── Payment Methods
│   ├── Billing
│   └── Refunds
└── Notification Service
    ├── Email
    ├── SMS
    └── Push Notifications
\\\`\\\`\\\`

### Service Boundaries
\\\`\\\`\\\`javascript
// User service boundary
class UserService {
  async createUser(userData) {
    // Validate user data
    const validatedData = await this.validateUserData(userData);
    
    // Create user record
    const user = await this.userRepository.create(validatedData);
    
    // Publish user created event
    await this.eventBus.publish('user.created', {
      userId: user.id,
      email: user.email,
      createdAt: user.createdAt
    });
    
    return user;
  }
  
  async updateUser(userId, updateData) {
    const existingUser = await this.userRepository.findById(userId);
    if (!existingUser) {
      throw new Error('User not found');
    }
    
    const updatedUser = await this.userRepository.update(userId, updateData);
    
    // Publish user updated event
    await this.eventBus.publish('user.updated', {
      userId: updatedUser.id,
      changes: updateData,
      updatedAt: updatedUser.updatedAt
    });
    
    return updatedUser;
  }
}
\\\`\\\`\\\`

## Data Management

### Database per Service
\\\`\\\`\\\`yaml
# Each service has its own database
Resources:
  UserDatabase:
    Type: AWS::DynamoDB::Table
    Properties:
      TableName: Users
      AttributeDefinitions:
        - AttributeName: userId
          AttributeType: S
      KeySchema:
        - AttributeName: userId
          KeyType: HASH
          
  OrderDatabase:
    Type: AWS::DynamoDB::Table
    Properties:
      TableName: Orders
      AttributeDefinitions:
        - AttributeName: orderId
          AttributeType: S
      KeySchema:
        - AttributeName: orderId
          KeyType: HASH
          
  ProductDatabase:
    Type: AWS::DynamoDB::Table
    Properties:
      TableName: Products
      AttributeDefinitions:
        - AttributeName: productId
          AttributeType: S
      KeySchema:
        - AttributeName: productId
          KeyType: HASH
\\\`\\\`\\\`

### Data Consistency Patterns
\\\`\\\`\\\`javascript
// Eventual consistency with event sourcing
class OrderService {
  async processOrder(orderData) {
    // Start with order creation
    const order = await this.createOrder(orderData);
    
    // Orchestrate saga for consistency
    const sagaId = uuidv4();
    await this.sagaOrchestrator.start(sagaId, 'process-order', {
      orderId: order.id,
      customerId: orderData.customerId,
      items: orderData.items,
      amount: orderData.amount
    });
    
    return order;
  }
}

// Saga orchestrator
class SagaOrchestrator {
  async start(sagaId, sagaType, data) {
    const saga = {
      id: sagaId,
      type: sagaType,
      status: 'started',
      data,
      steps: [],
      createdAt: new Date()
    };
    
    await this.sagaRepository.save(saga);
    await this.processNextStep(saga);
  }
  
  async processNextStep(saga) {
    const definition = this.getSagaDefinition(saga.type);
    const nextStep = definition.getNextStep(saga.steps);
    
    if (!nextStep) {
      await this.completeSaga(saga);
      return;
    }
    
    try {
      await this.executeStep(saga, nextStep);
    } catch (error) {
      await this.handleStepFailure(saga, nextStep, error);
    }
  }
}
\\\`\\\`\\\`

## Communication Patterns

### Synchronous Communication
\\\`\\\`\\\`javascript
// HTTP-based service communication
class CustomerService {
  constructor(orderServiceClient, paymentServiceClient) {
    this.orderService = orderServiceClient;
    this.paymentService = paymentServiceClient;
  }
  
  async getCustomerDashboard(customerId) {
    try {
      // Call multiple services synchronously
      const [customer, orders, paymentMethods] = await Promise.all([
        this.getCustomer(customerId),
        this.orderService.getCustomerOrders(customerId),
        this.paymentService.getPaymentMethods(customerId)
      ]);
      
      return {
        customer,
        recentOrders: orders.slice(0, 5),
        paymentMethods: paymentMethods.filter(pm => pm.active)
      };
    } catch (error) {
      // Handle service failures gracefully
      return this.buildFallbackDashboard(customerId, error);
    }
  }
  
  buildFallbackDashboard(customerId, error) {
    console.error('Dashboard service error:', error);
    
    return {
      customer: { id: customerId, name: 'Customer' },
      recentOrders: [],
      paymentMethods: [],
      notice: 'Some information is temporarily unavailable'
    };
  }
}
\\\`\\\`\\\`

### Asynchronous Communication
\\\`\\\`\\\`javascript
// Event-driven communication
class OrderService {
  async createOrder(orderData) {
    const order = await this.orderRepository.create(orderData);
    
    // Publish order created event
    await this.eventBus.publish('order.created', {
      orderId: order.id,
      customerId: order.customerId,
      items: order.items,
      total: order.total,
      createdAt: order.createdAt
    });
    
    return order;
  }
}

// Inventory service listens to order events
class InventoryService {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.setupEventHandlers();
  }
  
  setupEventHandlers() {
    this.eventBus.subscribe('order.created', this.handleOrderCreated.bind(this));
    this.eventBus.subscribe('order.cancelled', this.handleOrderCancelled.bind(this));
  }
  
  async handleOrderCreated(event) {
    const { orderId, items } = event.data;
    
    try {
      // Reserve inventory for order items
      for (const item of items) {
        await this.reserveInventory(item.productId, item.quantity);
      }
      
      // Publish inventory reserved event
      await this.eventBus.publish('inventory.reserved', {
        orderId,
        items,
        reservedAt: new Date()
      });
    } catch (error) {
      // Publish inventory reservation failed event
      await this.eventBus.publish('inventory.reservation_failed', {
        orderId,
        items,
        error: error.message,
        failedAt: new Date()
      });
    }
  }
}
\\\`\\\`\\\`

## Security Considerations

### Service-to-Service Authentication
\\\`\\\`\\\`javascript
// JWT-based service authentication
class ServiceAuthenticator {
  constructor(secretKey) {
    this.secretKey = secretKey;
  }
  
  generateServiceToken(serviceId, scopes) {
    const payload = {
      iss: 'microservices-platform',
      sub: serviceId,
      aud: 'api-gateway',
      scopes,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + (60 * 60) // 1 hour
    };
    
    return jwt.sign(payload, this.secretKey);
  }
  
  validateServiceToken(token) {
    try {
      const payload = jwt.verify(token, this.secretKey);
      return {
        valid: true,
        serviceId: payload.sub,
        scopes: payload.scopes
      };
    } catch (error) {
      return {
        valid: false,
        error: error.message
      };
    }
  }
}

// API Gateway authorization
exports.authorizer = async (event) => {
  const token = event.authorizationToken;
  const authenticator = new ServiceAuthenticator(process.env.JWT_SECRET);
  
  const validation = authenticator.validateServiceToken(token);
  
  if (!validation.valid) {
    throw new Error('Unauthorized');
  }
  
  return {
    principalId: validation.serviceId,
    policyDocument: {
      Version: '2012-10-17',
      Statement: [{
        Action: 'execute-api:Invoke',
        Effect: 'Allow',
        Resource: event.methodArn
      }]
    },
    context: {
      serviceId: validation.serviceId,
      scopes: validation.scopes.join(',')
    }
  };
};
\\\`\\\`\\\`

This foundation in serverless microservices design provides the architectural patterns and principles needed for building scalable, maintainable distributed systems.`
      },
      {
        id: 'event-driven-architecture',
        title: 'Event-Driven Architecture Patterns',
        description: 'Implement event-driven patterns for microservices communication.',
        order: 2,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'advanced',
        objectives: ['Implement event-driven patterns', 'Design async communication', 'Handle event ordering'],
        content: `# Event-Driven Architecture Patterns

## Event-Driven Architecture Overview

Event-driven architecture (EDA) enables microservices to communicate asynchronously through events, creating loosely coupled, highly scalable systems that can evolve independently.

### Core Concepts

1. **Events** - Immutable facts about something that happened
2. **Event Producers** - Services that publish events
3. **Event Consumers** - Services that react to events
4. **Event Store** - Persistent storage for events
5. **Event Bus** - Message routing infrastructure

## Event Types

### Domain Events
\\\`\\\`\\\`javascript
// Domain events represent business occurrences
class UserRegisteredEvent {
  constructor(userId, email, registeredAt) {
    this.eventType = 'user.registered';
    this.eventId = uuidv4();
    this.userId = userId;
    this.email = email;
    this.registeredAt = registeredAt;
    this.version = 1;
  }
}

class OrderPlacedEvent {
  constructor(orderId, customerId, items, total) {
    this.eventType = 'order.placed';
    this.eventId = uuidv4();
    this.orderId = orderId;
    this.customerId = customerId;
    this.items = items;
    this.total = total;
    this.placedAt = new Date();
    this.version = 1;
  }
}
\\\`\\\`\\\`

### Integration Events
\\\`\\\`\\\`javascript
// Integration events for service communication
class PaymentProcessedEvent {
  constructor(paymentId, orderId, amount, status) {
    this.eventType = 'payment.processed';
    this.eventId = uuidv4();
    this.paymentId = paymentId;
    this.orderId = orderId;
    this.amount = amount;
    this.status = status; // 'success' | 'failed'
    this.processedAt = new Date();
    this.correlationId = uuidv4();
  }
}

class InventoryUpdatedEvent {
  constructor(productId, previousQuantity, newQuantity, reason) {
    this.eventType = 'inventory.updated';
    this.eventId = uuidv4();
    this.productId = productId;
    this.previousQuantity = previousQuantity;
    this.newQuantity = newQuantity;
    this.reason = reason; // 'sale', 'restock', 'adjustment'
    this.updatedAt = new Date();
  }
}
\\\`\\\`\\\`

## Event Bus Implementation

### AWS EventBridge
\\\`\\\`\\\`yaml
# EventBridge setup
Resources:
  CustomEventBus:
    Type: AWS::Events::EventBus
    Properties:
      Name: microservices-event-bus
      
  OrderEventRule:
    Type: AWS::Events::Rule
    Properties:
      EventBusName: !Ref CustomEventBus
      EventPattern:
        source: ["order-service"]
        detail-type: ["Order Placed", "Order Cancelled"]
      Targets:
        - Arn: !GetAtt InventoryServiceFunction.Arn
          Id: "InventoryServiceTarget"
        - Arn: !GetAtt NotificationServiceFunction.Arn
          Id: "NotificationServiceTarget"
\\\`\\\`\\\`

### EventBridge Integration
\\\`\\\`\\\`javascript
// Publishing events to EventBridge
const { EventBridgeClient, PutEventsCommand } = require('@aws-sdk/client-eventbridge');

class EventPublisher {
  constructor() {
    this.eventBridge = new EventBridgeClient({ region: process.env.AWS_REGION });
    this.eventBusName = process.env.EVENT_BUS_NAME;
  }
  
  async publishEvent(event) {
    const params = {
      Entries: [{
        Source: event.source || 'microservices',
        DetailType: event.eventType,
        Detail: JSON.stringify(event),
        EventBusName: this.eventBusName,
        Time: new Date()
      }]
    };
    
    try {
      const command = new PutEventsCommand(params);
      const response = await this.eventBridge.send(command);
      
      console.log('Event published successfully:', response);
      return response;
    } catch (error) {
      console.error('Failed to publish event:', error);
      throw error;
    }
  }
  
  async publishDomainEvent(eventType, data, source = 'domain') {
    const event = {
      eventId: uuidv4(),
      eventType,
      source,
      data,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    return this.publishEvent(event);
  }
}

// Usage in service
exports.handler = async (event) => {
  const orderData = JSON.parse(event.body);
  
  // Create order
  const order = await createOrder(orderData);
  
  // Publish domain event
  const publisher = new EventPublisher();
  await publisher.publishDomainEvent('order.placed', {
    orderId: order.id,
    customerId: order.customerId,
    items: order.items,
    total: order.total
  }, 'order-service');
  
  return {
    statusCode: 201,
    body: JSON.stringify(order)
  };
};
\\\`\\\`\\\`

## Event Sourcing Pattern

### Event Store Implementation
\\\`\\\`\\\`javascript
// Event store for persisting events
class EventStore {
  constructor(dynamoClient) {
    this.dynamoClient = dynamoClient;
    this.tableName = process.env.EVENT_STORE_TABLE;
  }
  
  async appendEvent(streamId, event, expectedVersion) {
    const eventRecord = {
      streamId,
      eventId: event.eventId,
      eventType: event.eventType,
      eventData: JSON.stringify(event),
      eventVersion: expectedVersion + 1,
      timestamp: new Date().toISOString()
    };
    
    try {
      await this.dynamoClient.put({
        TableName: this.tableName,
        Item: eventRecord,
        ConditionExpression: 'attribute_not_exists(eventId)'
      }).promise();
      
      return eventRecord;
    } catch (error) {
      if (error.code === 'ConditionalCheckFailedException') {
        throw new Error('Concurrency conflict');
      }
      throw error;
    }
  }
  
  async getEvents(streamId, fromVersion = 0) {
    const params = {
      TableName: this.tableName,
      KeyConditionExpression: 'streamId = :streamId AND eventVersion > :fromVersion',
      ExpressionAttributeValues: {
        ':streamId': streamId,
        ':fromVersion': fromVersion
      },
      ScanIndexForward: true
    };
    
    const result = await this.dynamoClient.query(params).promise();
    
    return result.Items.map(item => ({
      ...JSON.parse(item.eventData),
      eventVersion: item.eventVersion,
      timestamp: item.timestamp
    }));
  }
}
\\\`\\\`\\\`

### Aggregate with Event Sourcing
\\\`\\\`\\\`javascript
// Order aggregate using event sourcing
class OrderAggregate {
  constructor(orderId) {
    this.id = orderId;
    this.customerId = null;
    this.items = [];
    this.status = 'draft';
    this.total = 0;
    this.version = 0;
    this.uncommittedEvents = [];
  }
  
  // Command: Place Order
  placeOrder(customerId, items) {
    if (this.status !== 'draft') {
      throw new Error('Order cannot be placed');
    }
    
    const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    this.raiseEvent(new OrderPlacedEvent(this.id, customerId, items, total));
  }
  
  // Command: Cancel Order
  cancelOrder(reason) {
    if (this.status !== 'placed') {
      throw new Error('Order cannot be cancelled');
    }
    
    this.raiseEvent(new OrderCancelledEvent(this.id, reason));
  }
  
  // Event handlers
  apply(event) {
    switch (event.eventType) {
      case 'order.placed':
        this.customerId = event.customerId;
        this.items = event.items;
        this.total = event.total;
        this.status = 'placed';
        break;
        
      case 'order.cancelled':
        this.status = 'cancelled';
        break;
        
      default:
        throw new Error(\\\`Unknown event type: \\\${event.eventType}\\\`);
    }
    
    this.version++;
  }
  
  raiseEvent(event) {
    this.apply(event);
    this.uncommittedEvents.push(event);
  }
  
  getUncommittedEvents() {
    return this.uncommittedEvents;
  }
  
  clearUncommittedEvents() {
    this.uncommittedEvents = [];
  }
  
  // Rebuild aggregate from events
  static fromEvents(orderId, events) {
    const aggregate = new OrderAggregate(orderId);
    
    events.forEach(event => {
      aggregate.apply(event);
    });
    
    return aggregate;
  }
}
\\\`\\\`\\\`

## CQRS (Command Query Responsibility Segregation)

### Command Side
\\\`\\\`\\\`javascript
// Command handlers for write operations
class OrderCommandHandler {
  constructor(eventStore, eventPublisher) {
    this.eventStore = eventStore;
    this.eventPublisher = eventPublisher;
  }
  
  async handlePlaceOrder(command) {
    const { orderId, customerId, items } = command;
    
    // Load aggregate from events
    const events = await this.eventStore.getEvents(orderId);
    const order = OrderAggregate.fromEvents(orderId, events);
    
    // Execute command
    order.placeOrder(customerId, items);
    
    // Save new events
    const uncommittedEvents = order.getUncommittedEvents();
    for (const event of uncommittedEvents) {
      await this.eventStore.appendEvent(orderId, event, order.version - 1);
      await this.eventPublisher.publishEvent(event);
    }
    
    order.clearUncommittedEvents();
    
    return { orderId, status: 'placed' };
  }
  
  async handleCancelOrder(command) {
    const { orderId, reason } = command;
    
    const events = await this.eventStore.getEvents(orderId);
    const order = OrderAggregate.fromEvents(orderId, events);
    
    order.cancelOrder(reason);
    
    const uncommittedEvents = order.getUncommittedEvents();
    for (const event of uncommittedEvents) {
      await this.eventStore.appendEvent(orderId, event, order.version - 1);
      await this.eventPublisher.publishEvent(event);
    }
    
    return { orderId, status: 'cancelled' };
  }
}
\\\`\\\`\\\`

### Query Side
\\\`\\\`\\\`javascript
// Read models for query operations
class OrderProjection {
  constructor(dynamoClient) {
    this.dynamoClient = dynamoClient;
    this.tableName = process.env.ORDER_PROJECTION_TABLE;
  }
  
  async handleOrderPlaced(event) {
    const orderView = {
      orderId: event.orderId,
      customerId: event.customerId,
      items: event.items,
      total: event.total,
      status: 'placed',
      placedAt: event.placedAt,
      updatedAt: event.placedAt
    };
    
    await this.dynamoClient.put({
      TableName: this.tableName,
      Item: orderView
    }).promise();
  }
  
  async handleOrderCancelled(event) {
    await this.dynamoClient.update({
      TableName: this.tableName,
      Key: { orderId: event.orderId },
      UpdateExpression: 'SET #status = :status, #updatedAt = :updatedAt',
      ExpressionAttributeNames: {
        '#status': 'status',
        '#updatedAt': 'updatedAt'
      },
      ExpressionAttributeValues: {
        ':status': 'cancelled',
        ':updatedAt': new Date().toISOString()
      }
    }).promise();
  }
  
  async getOrder(orderId) {
    const result = await this.dynamoClient.get({
      TableName: this.tableName,
      Key: { orderId }
    }).promise();
    
    return result.Item;
  }
  
  async getCustomerOrders(customerId) {
    const result = await this.dynamoClient.query({
      TableName: this.tableName,
      IndexName: 'CustomerIndex',
      KeyConditionExpression: 'customerId = :customerId',
      ExpressionAttributeValues: {
        ':customerId': customerId
      }
    }).promise();
    
    return result.Items;
  }
}
\\\`\\\`\\\`

## Saga Pattern

### Orchestration-based Saga
\\\`\\\`\\\`javascript
// Saga orchestrator for complex workflows
class OrderSagaOrchestrator {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.setupEventHandlers();
  }
  
  setupEventHandlers() {
    this.eventBus.subscribe('order.placed', this.handleOrderPlaced.bind(this));
    this.eventBus.subscribe('payment.processed', this.handlePaymentProcessed.bind(this));
    this.eventBus.subscribe('inventory.reserved', this.handleInventoryReserved.bind(this));
    this.eventBus.subscribe('shipping.scheduled', this.handleShippingScheduled.bind(this));
  }
  
  async handleOrderPlaced(event) {
    const sagaId = uuidv4();
    const saga = {
      id: sagaId,
      orderId: event.orderId,
      customerId: event.customerId,
      total: event.total,
      status: 'started',
      steps: [],
      createdAt: new Date()
    };
    
    await this.saveSaga(saga);
    
    // Start saga: process payment
    await this.eventBus.publish('command.process_payment', {
      sagaId,
      orderId: event.orderId,
      customerId: event.customerId,
      amount: event.total
    });
  }
  
  async handlePaymentProcessed(event) {
    const saga = await this.getSaga(event.sagaId);
    
    if (event.status === 'success') {
      saga.steps.push({ step: 'payment', status: 'completed' });
      await this.saveSaga(saga);
      
      // Next step: reserve inventory
      await this.eventBus.publish('command.reserve_inventory', {
        sagaId: saga.id,
        orderId: saga.orderId,
        items: event.items
      });
    } else {
      // Payment failed - compensate
      await this.compensateOrder(saga, 'payment_failed');
    }
  }
  
  async handleInventoryReserved(event) {
    const saga = await this.getSaga(event.sagaId);
    
    if (event.status === 'success') {
      saga.steps.push({ step: 'inventory', status: 'completed' });
      await this.saveSaga(saga);
      
      // Next step: schedule shipping
      await this.eventBus.publish('command.schedule_shipping', {
        sagaId: saga.id,
        orderId: saga.orderId,
        items: event.items
      });
    } else {
      // Inventory reservation failed - compensate
      await this.compensateOrder(saga, 'inventory_failed');
    }
  }
  
  async compensateOrder(saga, reason) {
    saga.status = 'compensating';
    saga.compensationReason = reason;
    
    // Reverse completed steps
    for (const step of saga.steps.reverse()) {
      switch (step.step) {
        case 'payment':
          await this.eventBus.publish('command.refund_payment', {
            orderId: saga.orderId,
            reason
          });
          break;
        case 'inventory':
          await this.eventBus.publish('command.release_inventory', {
            orderId: saga.orderId,
            reason
          });
          break;
      }
    }
    
    saga.status = 'compensated';
    await this.saveSaga(saga);
    
    // Cancel order
    await this.eventBus.publish('command.cancel_order', {
      orderId: saga.orderId,
      reason
    });
  }
}
\\\`\\\`\\\`

## Event Replay and Recovery

### Event Replay Mechanism
\\\`\\\`\\\`javascript
// Event replay for rebuilding projections
class EventReplayService {
  constructor(eventStore) {
    this.eventStore = eventStore;
  }
  
  async replayEvents(fromTimestamp, toTimestamp, eventTypes = []) {
    const events = await this.eventStore.getEventsByTimeRange(
      fromTimestamp, 
      toTimestamp, 
      eventTypes
    );
    
    console.log(\\\`Replaying \\\${events.length} events...\\\`);
    
    for (const event of events) {
      try {
        await this.processEvent(event);
        console.log(\\\`Replayed event: \\\${event.eventType} - \\\${event.eventId}\\\`);
      } catch (error) {
        console.error(\\\`Failed to replay event \\\${event.eventId}:\\\`, error);
        // Continue with other events or implement retry logic
      }
    }
    
    console.log('Event replay completed');
  }
  
  async rebuildProjection(projectionName, fromVersion = 0) {
    const projection = this.getProjection(projectionName);
    
    // Clear existing projection data
    await projection.clear();
    
    // Replay all relevant events
    const events = await this.eventStore.getAllEvents(fromVersion);
    
    for (const event of events) {
      if (projection.canHandle(event.eventType)) {
        await projection.handle(event);
      }
    }
    
    console.log(\\\`Projection \\\${projectionName} rebuilt successfully\\\`);
  }
}
\\\`\\\`\\\`

This comprehensive guide provides the patterns and implementations needed for building robust event-driven microservices architectures.`
      },
      {
        id: 'distributed-data-management',
        title: 'Distributed Data Management',
        description: 'Manage data consistency and transactions across distributed microservices.',
        order: 3,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'advanced',
        objectives: ['Manage distributed data', 'Implement data consistency', 'Handle transactions'],
        content: `# Distributed Data Management

## Distributed Data Challenges

Managing data across microservices presents unique challenges that traditional monolithic applications don't face. Each service owns its data, leading to complex scenarios around consistency, transactions, and querying.

### Key Challenges

1. **Data Consistency** - Maintaining consistency across services
2. **Distributed Transactions** - Coordinating changes across multiple services
3. **Data Querying** - Joining data from different services
4. **Schema Evolution** - Managing changes to data structures
5. **Data Synchronization** - Keeping replicated data in sync

## Database-per-Service Pattern

### Service Data Ownership
\\\`\\\`\\\`javascript
// Each service owns its data completely
class UserService {
  constructor() {
    this.userRepository = new UserRepository();
    this.eventPublisher = new EventPublisher();
  }
  
  async createUser(userData) {
    // Validate within service boundary
    const validatedData = this.validateUserData(userData);
    
    // Create in service's database
    const user = await this.userRepository.create(validatedData);
    
    // Publish event for other services
    await this.eventPublisher.publish('user.created', {
      userId: user.id,
      email: user.email,
      profile: user.profile,
      createdAt: user.createdAt
    });
    
    return user;
  }
  
  async getUserById(userId) {
    return await this.userRepository.findById(userId);
  }
  
  // Only this service can modify user data
  async updateUser(userId, updateData) {
    const user = await this.userRepository.update(userId, updateData);
    
    await this.eventPublisher.publish('user.updated', {
      userId: user.id,
      changes: updateData,
      updatedAt: user.updatedAt
    });
    
    return user;
  }
}
\\\`\\\`\\\`

### Database Technology Choice
\\\`\\\`\\\`yaml
# Different services can use different databases
Resources:
  # User service - DynamoDB for flexible schema
  UserDatabase:
    Type: AWS::DynamoDB::Table
    Properties:
      TableName: Users
      BillingMode: PAY_PER_REQUEST
      AttributeDefinitions:
        - AttributeName: userId
          AttributeType: S
      KeySchema:
        - AttributeName: userId
          KeyType: HASH
          
  # Order service - RDS for complex relationships
  OrderDatabase:
    Type: AWS::RDS::DBInstance
    Properties:
      DBInstanceClass: db.t3.micro
      Engine: postgres
      MasterUsername: orderservice
      AllocatedStorage: 20
      
  # Product catalog - OpenSearch for full-text search
  ProductSearchCluster:
    Type: AWS::OpenSearch::Domain
    Properties:
      DomainName: product-catalog
      ElasticsearchVersion: 7.10
\\\`\\\`\\\`

## Data Consistency Patterns

### Eventual Consistency
\\\`\\\`\\\`javascript
// Order service maintains eventual consistency
class OrderService {
  constructor() {
    this.orderRepository = new OrderRepository();
    this.eventBus = new EventBus();
    this.setupEventHandlers();
  }
  
  setupEventHandlers() {
    // Listen to events from other services
    this.eventBus.subscribe('user.updated', this.handleUserUpdated.bind(this));
    this.eventBus.subscribe('product.price_changed', this.handlePriceChanged.bind(this));
  }
  
  async handleUserUpdated(event) {
    const { userId, changes } = event.data;
    
    // Update local user data cache
    if (changes.email || changes.name) {
      await this.orderRepository.updateUserInfo(userId, {
        email: changes.email,
        name: changes.name
      });
    }
    
    console.log(\\\`Updated user info for orders: \\\${userId}\\\`);
  }
  
  async handlePriceChanged(event) {
    const { productId, newPrice, oldPrice } = event.data;
    
    // Update pending orders with old price
    const pendingOrders = await this.orderRepository.findPendingOrdersByProduct(productId);
    
    for (const order of pendingOrders) {
      await this.updateOrderItemPrice(order.id, productId, newPrice);
      
      // Notify customer about price change
      await this.eventBus.publish('order.price_updated', {
        orderId: order.id,
        customerId: order.customerId,
        productId,
        oldPrice,
        newPrice
      });
    }
  }
}
\\\`\\\`\\\`

### Strong Consistency with Saga
\\\`\\\`\\\`javascript
// Two-phase commit simulation with saga pattern
class DistributedTransactionManager {
  constructor() {
    this.sagaRepository = new SagaRepository();
    this.eventBus = new EventBus();
  }
  
  async executeDistributedTransaction(transactionId, operations) {
    const saga = {
      id: transactionId,
      operations,
      status: 'started',
      completedOperations: [],
      compensationRequired: false,
      createdAt: new Date()
    };
    
    await this.sagaRepository.save(saga);
    
    try {
      // Execute operations in sequence
      for (const operation of operations) {
        await this.executeOperation(saga, operation);
      }
      
      // All operations succeeded
      saga.status = 'completed';
      await this.sagaRepository.save(saga);
      
      return { success: true, transactionId };
    } catch (error) {
      // Compensate for partial completion
      await this.compensateTransaction(saga, error);
      return { success: false, error: error.message, transactionId };
    }
  }
  
  async executeOperation(saga, operation) {
    try {
      console.log(\\\`Executing operation: \\\${operation.type}\\\`);
      
      switch (operation.type) {
        case 'reserve_inventory':
          await this.reserveInventory(operation.productId, operation.quantity);
          break;
        case 'process_payment':
          await this.processPayment(operation.customerId, operation.amount);
          break;
        case 'create_shipment':
          await this.createShipment(operation.orderId, operation.address);
          break;
        default:
          throw new Error(\\\`Unknown operation: \\\${operation.type}\\\`);
      }
      
      saga.completedOperations.push(operation);
      await this.sagaRepository.save(saga);
    } catch (error) {
      saga.compensationRequired = true;
      saga.failedOperation = operation;
      await this.sagaRepository.save(saga);
      throw error;
    }
  }
  
  async compensateTransaction(saga, error) {
    console.log('Starting compensation for failed transaction');
    saga.status = 'compensating';
    await this.sagaRepository.save(saga);
    
    // Reverse completed operations in reverse order
    for (const operation of saga.completedOperations.reverse()) {
      try {
        await this.compensateOperation(operation);
      } catch (compensationError) {
        console.error('Compensation failed:', compensationError);
        // Log for manual intervention
      }
    }
    
    saga.status = 'compensated';
    saga.compensationReason = error.message;
    await this.sagaRepository.save(saga);
  }
  
  async compensateOperation(operation) {
    switch (operation.type) {
      case 'reserve_inventory':
        await this.releaseInventory(operation.productId, operation.quantity);
        break;
      case 'process_payment':
        await this.refundPayment(operation.customerId, operation.amount);
        break;
      case 'create_shipment':
        await this.cancelShipment(operation.shipmentId);
        break;
    }
  }
}
\\\`\\\`\\\`

## Data Synchronization Patterns

### Change Data Capture (CDC)
\\\`\\\`\\\`javascript
// DynamoDB Streams for change data capture
exports.handler = async (event) => {
  for (const record of event.Records) {
    const { eventName, dynamodb } = record;
    
    switch (eventName) {
      case 'INSERT':
        await handleUserCreated(dynamodb.NewImage);
        break;
      case 'MODIFY':
        await handleUserModified(dynamodb.NewImage, dynamodb.OldImage);
        break;
      case 'REMOVE':
        await handleUserDeleted(dynamodb.OldImage);
        break;
    }
  }
};

async function handleUserCreated(newImage) {
  const user = unmarshall(newImage);
  
  // Propagate to other services that need user data
  await publishEvent('user.created', {
    userId: user.userId,
    email: user.email,
    name: user.name,
    createdAt: user.createdAt
  });
  
  // Update search index
  await updateSearchIndex('users', user.userId, {
    name: user.name,
    email: user.email
  });
}

async function handleUserModified(newImage, oldImage) {
  const newUser = unmarshall(newImage);
  const oldUser = unmarshall(oldImage);
  
  // Detect what changed
  const changes = detectChanges(oldUser, newUser);
  
  if (changes.length > 0) {
    await publishEvent('user.updated', {
      userId: newUser.userId,
      changes: changes,
      updatedAt: newUser.updatedAt
    });
    
    // Update dependent services
    await syncUserDataToServices(newUser.userId, changes);
  }
}
\\\`\\\`\\\`

### Event Sourcing for Data Sync
\\\`\\\`\\\`javascript
// Event sourcing for maintaining consistency
class CustomerEventProjection {
  constructor() {
    this.eventBus = new EventBus();
    this.projectionStore = new ProjectionStore();
    this.setupEventHandlers();
  }
  
  setupEventHandlers() {
    this.eventBus.subscribe('user.created', this.handleUserCreated.bind(this));
    this.eventBus.subscribe('user.updated', this.handleUserUpdated.bind(this));
    this.eventBus.subscribe('order.placed', this.handleOrderPlaced.bind(this));
    this.eventBus.subscribe('payment.processed', this.handlePaymentProcessed.bind(this));
  }
  
  async handleUserCreated(event) {
    const customerProjection = {
      customerId: event.userId,
      name: event.name,
      email: event.email,
      totalOrders: 0,
      totalSpent: 0,
      lastOrderDate: null,
      status: 'active',
      createdAt: event.createdAt,
      updatedAt: event.createdAt
    };
    
    await this.projectionStore.saveCustomerProjection(customerProjection);
  }
  
  async handleOrderPlaced(event) {
    const customer = await this.projectionStore.getCustomerProjection(event.customerId);
    
    customer.totalOrders += 1;
    customer.totalSpent += event.total;
    customer.lastOrderDate = event.placedAt;
    customer.updatedAt = new Date();
    
    // Update customer tier based on spending
    customer.tier = this.calculateCustomerTier(customer.totalSpent);
    
    await this.projectionStore.saveCustomerProjection(customer);
  }
  
  calculateCustomerTier(totalSpent) {
    if (totalSpent >= 10000) return 'platinum';
    if (totalSpent >= 5000) return 'gold';
    if (totalSpent >= 1000) return 'silver';
    return 'bronze';
  }
}
\\\`\\\`\\\`

## Cross-Service Queries

### API Composition Pattern
\\\`\\\`\\\`javascript
// Compose data from multiple services
class CustomerDashboardService {
  constructor() {
    this.userService = new UserServiceClient();
    this.orderService = new OrderServiceClient();
    this.paymentService = new PaymentServiceClient();
    this.loyaltyService = new LoyaltyServiceClient();
  }
  
  async getCustomerDashboard(customerId) {
    try {
      // Fetch data from multiple services in parallel
      const [
        customer,
        recentOrders,
        paymentMethods,
        loyaltyPoints,
        recommendations
      ] = await Promise.allSettled([
        this.userService.getCustomer(customerId),
        this.orderService.getRecentOrders(customerId, 5),
        this.paymentService.getPaymentMethods(customerId),
        this.loyaltyService.getPoints(customerId),
        this.getRecommendations(customerId)
      ]);
      
      // Handle partial failures gracefully
      return {
        customer: this.handleResult(customer, 'Customer data unavailable'),
        recentOrders: this.handleResult(recentOrders, []),
        paymentMethods: this.handleResult(paymentMethods, []),
        loyaltyPoints: this.handleResult(loyaltyPoints, { points: 0 }),
        recommendations: this.handleResult(recommendations, [])
      };
    } catch (error) {
      console.error('Dashboard composition error:', error);
      throw new Error('Unable to load dashboard');
    }
  }
  
  handleResult(result, fallback) {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      console.warn('Service call failed:', result.reason);
      return fallback;
    }
  }
  
  async getRecommendations(customerId) {
    // Combine data from multiple sources for recommendations
    const [orderHistory, browsing, similarCustomers] = await Promise.all([
      this.orderService.getOrderHistory(customerId),
      this.analyticsService.getBrowsingHistory(customerId),
      this.recommendationService.getSimilarCustomers(customerId)
    ]);
    
    return this.calculateRecommendations(orderHistory, browsing, similarCustomers);
  }
}
\\\`\\\`\\\`

### CQRS with Read Models
\\\`\\\`\\\`javascript
// Materialized views for complex queries
class OrderSummaryProjection {
  constructor() {
    this.eventBus = new EventBus();
    this.projectionStore = new ProjectionStore();
    this.setupEventHandlers();
  }
  
  setupEventHandlers() {
    this.eventBus.subscribe('order.placed', this.handleOrderPlaced.bind(this));
    this.eventBus.subscribe('order.shipped', this.handleOrderShipped.bind(this));
    this.eventBus.subscribe('order.delivered', this.handleOrderDelivered.bind(this));
    this.eventBus.subscribe('payment.processed', this.handlePaymentProcessed.bind(this));
  }
  
  async handleOrderPlaced(event) {
    const orderSummary = {
      orderId: event.orderId,
      customerId: event.customerId,
      customerName: await this.getCustomerName(event.customerId),
      items: event.items,
      totalAmount: event.total,
      status: 'placed',
      placedAt: event.placedAt,
      estimatedDelivery: this.calculateEstimatedDelivery(event.items),
      paymentStatus: 'pending',
      shippingStatus: 'pending'
    };
    
    await this.projectionStore.saveOrderSummary(orderSummary);
  }
  
  async handlePaymentProcessed(event) {
    await this.projectionStore.updateOrderSummary(event.orderId, {
      paymentStatus: event.status,
      paymentMethod: event.paymentMethod,
      paymentProcessedAt: event.processedAt
    });
  }
  
  async handleOrderShipped(event) {
    await this.projectionStore.updateOrderSummary(event.orderId, {
      shippingStatus: 'shipped',
      trackingNumber: event.trackingNumber,
      shippedAt: event.shippedAt,
      carrier: event.carrier
    });
  }
  
  // Query methods
  async getOrderSummary(orderId) {
    return await this.projectionStore.getOrderSummary(orderId);
  }
  
  async getCustomerOrderSummaries(customerId, limit = 10) {
    return await this.projectionStore.getCustomerOrderSummaries(customerId, limit);
  }
  
  async getOrdersByStatus(status, limit = 50) {
    return await this.projectionStore.getOrdersByStatus(status, limit);
  }
}
\\\`\\\`\\\`

## Data Migration and Schema Evolution

### Backward Compatible Changes
\\\`\\\`\\\`javascript
// Handle schema evolution gracefully
class UserService {
  async getUser(userId) {
    const user = await this.userRepository.findById(userId);
    
    // Handle different schema versions
    return this.normalizeUserData(user);
  }
  
  normalizeUserData(userData) {
    // Handle v1 schema (legacy)
    if (!userData.version || userData.version === 1) {
      return {
        id: userData.id,
        email: userData.email,
        name: userData.fullName || userData.name, // Handle field rename
        profile: {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          phone: userData.phone || null
        },
        preferences: userData.preferences || {},
        version: 2 // Upgrade to current version
      };
    }
    
    // Handle v2 schema (current)
    if (userData.version === 2) {
      return userData;
    }
    
    throw new Error(\\\`Unsupported user data version: \\\${userData.version}\\\`);
  }
  
  async updateUser(userId, updateData) {
    // Always save in current schema version
    const normalizedData = {
      ...updateData,
      version: 2,
      updatedAt: new Date()
    };
    
    return await this.userRepository.update(userId, normalizedData);
  }
}
\\\`\\\`\\\`

### Data Migration Strategy
\\\`\\\`\\\`javascript
// Gradual data migration
class DataMigrationService {
  constructor() {
    this.userRepository = new UserRepository();
    this.eventPublisher = new EventPublisher();
  }
  
  async migrateUserDataToV2() {
    console.log('Starting user data migration to v2...');
    
    let lastEvaluatedKey = null;
    let migratedCount = 0;
    
    do {
      // Process in batches to avoid timeouts
      const batch = await this.userRepository.scanUsers({
        limit: 100,
        lastEvaluatedKey,
        filterExpression: 'attribute_not_exists(version) OR version = :v1',
        expressionAttributeValues: { ':v1': 1 }
      });
      
      for (const user of batch.items) {
        try {
          const migratedUser = this.migrateUserToV2(user);
          await this.userRepository.update(user.id, migratedUser);
          
          // Publish migration event
          await this.eventPublisher.publish('user.migrated', {
            userId: user.id,
            fromVersion: user.version || 1,
            toVersion: 2,
            migratedAt: new Date()
          });
          
          migratedCount++;
        } catch (error) {
          console.error(\\\`Failed to migrate user \\\${user.id}:\\\`, error);
        }
      }
      
      lastEvaluatedKey = batch.lastEvaluatedKey;
      console.log(\\\`Migrated \\\${migratedCount} users so far...\\\`);
      
    } while (lastEvaluatedKey);
    
    console.log(\\\`Migration completed. Total migrated: \\\${migratedCount}\\\`);
  }
  
  migrateUserToV2(user) {
    return {
      ...user,
      name: user.fullName || user.name,
      profile: {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || null,
        address: user.address || null
      },
      preferences: user.preferences || {
        emailNotifications: true,
        smsNotifications: false
      },
      version: 2,
      migratedAt: new Date()
    };
  }
}
\\\`\\\`\\\`

This comprehensive guide provides the patterns and strategies needed for managing data effectively in distributed microservices architectures.`
      },
      {
        id: 'monitoring-deployment',
        title: 'Monitoring & Deployment',
        description: 'Implement comprehensive monitoring, deployment strategies, and operational excellence for serverless microservices.',
        order: 4,
        estimatedTime: '90 minutes',
        duration: '90 minutes',
        difficulty: 'advanced',
        objectives: ['Implement monitoring', 'Set up deployment pipelines', 'Ensure operational excellence'],
        content: `# Monitoring & Deployment

## Learning Objectives
- Implement comprehensive monitoring for microservices
- Set up distributed tracing and logging
- Master deployment strategies and CI/CD
- Handle service mesh and API gateway patterns

## Monitoring Microservices

### Distributed Tracing
Tracing requests across multiple services:

\\\`\\\`\\\`javascript
// OpenTelemetry setup
const { NodeTracerProvider } = require('@opentelemetry/sdk-node');
const { getNodeAutoInstrumentations } = require('@opentelemetry/auto-instrumentations-node');

const provider = new NodeTracerProvider({
  instrumentations: [getNodeAutoInstrumentations()]
});

provider.register();
\\\`\\\`\\\`

### Centralized Logging

\\\`\\\`\\\`javascript
// Structured logging with correlation IDs
const logger = require('winston');

const correlationLogger = {
  info: (message, correlationId, metadata = {}) => {
    logger.info(message, {
      correlationId,
      service: 'user-service',
      ...metadata
    });
  }
};

// Usage in service
exports.handler = async (event) => {
  const correlationId = event.headers['x-correlation-id'] || uuidv4();
  
  correlationLogger.info('Processing user request', correlationId, {
    userId: event.pathParameters.id
  });
  
  return {
    statusCode: 200,
    headers: {
      'x-correlation-id': correlationId
    },
    body: JSON.stringify(result)
  };
};
\\\`\\\`\\\`

## Metrics and Observability

### Custom Metrics

\\\`\\\`\\\`javascript
// CloudWatch custom metrics
const AWS = require('aws-sdk');
const cloudwatch = new AWS.CloudWatch();

const putMetric = async (metricName, value, unit = 'Count') => {
  const params = {
    Namespace: 'Microservices/UserService',
    MetricData: [{
      MetricName: metricName,
      Value: value,
      Unit: unit,
      Timestamp: new Date()
    }]
  };
  
  await cloudwatch.putMetricData(params).promise();
};

// Usage
await putMetric('UserCreated', 1);
await putMetric('ResponseTime', responseTime, 'Milliseconds');
\\\`\\\`\\\`

### Health Checks

\\\`\\\`\\\`javascript
// Health check endpoint
exports.healthCheck = async () => {
  const checks = {
    database: await checkDatabase(),
    externalAPI: await checkExternalAPI(),
    memory: process.memoryUsage().heapUsed < 100 * 1024 * 1024
  };
  
  const healthy = Object.values(checks).every(check => check === true);
  
  return {
    statusCode: healthy ? 200 : 503,
    body: JSON.stringify({
      status: healthy ? 'healthy' : 'unhealthy',
      checks,
      timestamp: new Date().toISOString()
    })
  };
};
\\\`\\\`\\\`

## Deployment Strategies

### Blue-Green Deployment

\\\`\\\`\\\`yaml
# AWS SAM template for blue-green deployment
AWSTemplateFormatVersion: '2010-09-09'
Transform: AWS::Serverless-2016-10-31

Parameters:
  Stage:
    Type: String
    Default: blue

Resources:
  UserServiceFunction:
    Type: AWS::Serverless::Function
    Properties:
      FunctionName: !Sub "user-service-\${Stage}"
      CodeUri: src/
      Handler: user.handler
      Runtime: nodejs18.x
      AutoPublishAlias: live
      DeploymentPreference:
        Type: Canary10Percent5Minutes
        Hooks:
          PreTraffic: !Ref PreTrafficHook
          PostTraffic: !Ref PostTrafficHook
\\\`\\\`\\\`

### Canary Deployments

\\\`\\\`\\\`javascript
// Lambda@Edge for traffic splitting
exports.handler = (event, context, callback) => {
  const request = event.Records[0].cf.request;
  const headers = request.headers;
  
  // Canary logic - 10% to new version
  const canaryWeight = 0.1;
  const random = Math.random();
  
  if (random < canaryWeight) {
    request.origin = {
      custom: {
        domainName: 'api-v2.example.com',
        port: 443,
        protocol: 'https'
      }
    };
  }
  
  callback(null, request);
};
\\\`\\\`\\\`

## Infrastructure as Code

### CloudFormation/CDK

\\\`\\\`\\\`yaml
# CloudFormation template for microservice
Resources:
  UserService:
    Type: AWS::Lambda::Function
    Properties:
      FunctionName: !Sub "\${Environment}-user-service"
      Runtime: nodejs18.x
      Handler: index.handler
      Code:
        ZipFile: |
          exports.handler = async (event) => {
            return { statusCode: 200, body: 'Hello World' };
          };
      Environment:
        Variables:
          DB_HOST: !Ref DatabaseHost
          LOG_LEVEL: !Ref LogLevel

  UserServiceRole:
    Type: AWS::IAM::Role
    Properties:
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              Service: lambda.amazonaws.com
            Action: sts:AssumeRole
      ManagedPolicyArns:
        - arn:aws:iam::aws:policy/service-role/AWSLambdaBasicExecutionRole
\\\`\\\`\\\`

### Terraform Configuration

\\\`\\\`\\\`hcl
# Terraform for microservices infrastructure
resource "aws_lambda_function" "user_service" {
  filename         = "user-service.zip"
  function_name    = "\${var.environment}-user-service"
  role            = aws_iam_role.lambda_role.arn
  handler         = "index.handler"
  runtime         = "nodejs18.x"
  
  environment {
    variables = {
      DB_HOST = var.db_host
      LOG_LEVEL = var.log_level
    }
  }
  
  tags = {
    Environment = var.environment
    Service = "user-service"
  }
}

resource "aws_api_gateway_integration" "user_service" {
  rest_api_id = aws_api_gateway_rest_api.main.id
  resource_id = aws_api_gateway_resource.users.id
  http_method = aws_api_gateway_method.users_post.http_method
  
  integration_http_method = "POST"
  type                   = "AWS_PROXY"
  uri                    = aws_lambda_function.user_service.invoke_arn
}
\\\`\\\`\\\`

## CI/CD Pipeline

### GitHub Actions

\\\`\\\`\\\`yaml
# .github/workflows/deploy.yml
name: Deploy Microservices

on:
  push:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      - uses: actions/setup-node@v3
        with:
          node-version: '18'
      - run: npm ci
      - run: npm test
      - run: npm run lint

  deploy:
    needs: test
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      - name: Configure AWS credentials
        uses: aws-actions/configure-aws-credentials@v2
        with:
          aws-access-key-id: \${{ secrets.AWS_ACCESS_KEY_ID }}
          aws-secret-access-key: \${{ secrets.AWS_SECRET_ACCESS_KEY }}
          aws-region: us-east-1
      
      - name: Deploy with SAM
        run: |
          sam build
          sam deploy --no-confirm-changeset --no-fail-on-empty-changeset
\\\`\\\`\\\`

### AWS CodePipeline

\\\`\\\`\\\`yaml
# buildspec.yml for CodeBuild
version: 0.2

phases:
  install:
    runtime-versions:
      nodejs: 18
  pre_build:
    commands:
      - npm ci
      - npm run test
  build:
    commands:
      - npm run build
      - sam build
  post_build:
    commands:
      - sam package --s3-bucket \$BUCKET_NAME --output-template-file packaged.yaml

artifacts:
  files:
    - packaged.yaml
\\\`\\\`\\\`

## Service Mesh

### AWS App Mesh

\\\`\\\`\\\`yaml
# App Mesh configuration
apiVersion: appmesh.k8s.aws/v1beta2
kind: VirtualService
metadata:
  name: user-service
spec:
  awsName: user-service
  provider:
    virtualRouter:
      virtualRouterRef:
        name: user-service-router

---
apiVersion: appmesh.k8s.aws/v1beta2
kind: VirtualRouter
metadata:
  name: user-service-router
spec:
  listeners:
    - portMapping:
        port: 80
        protocol: http
  routes:
    - name: user-service-route
      httpRoute:
        match:
          prefix: /
        action:
          weightedTargets:
            - virtualNodeRef:
                name: user-service-v1
              weight: 90
            - virtualNodeRef:
                name: user-service-v2
              weight: 10
\\\`\\\`\\\`

## API Gateway Patterns

### Rate Limiting and Throttling

\\\`\\\`\\\`javascript
// Custom rate limiting with Redis
const redis = require('redis');
const client = redis.createClient();

const rateLimit = async (userId, limit = 100, window = 3600) => {
  const key = \\\`rate_limit:\\\${userId}:\\\${Math.floor(Date.now() / (window * 1000))}\\\`;
  
  const current = await client.incr(key);
  if (current === 1) {
    await client.expire(key, window);
  }
  
  return {
    allowed: current <= limit,
    remaining: Math.max(0, limit - current),
    resetTime: Math.ceil(Date.now() / 1000) + window
  };
};

exports.handler = async (event) => {
  const userId = event.requestContext.authorizer.userId;
  const rateLimitResult = await rateLimit(userId);
  
  if (!rateLimitResult.allowed) {
    return {
      statusCode: 429,
      headers: {
        'X-RateLimit-Remaining': rateLimitResult.remaining,
        'X-RateLimit-Reset': rateLimitResult.resetTime
      },
      body: JSON.stringify({ error: 'Rate limit exceeded' })
    };
  }
  
  // Process request...
};
\\\`\\\`\\\`

### Circuit Breaker Pattern

\\\`\\\`\\\`javascript
// Circuit breaker implementation
class CircuitBreaker {
  constructor(threshold = 5, timeout = 60000, monitor = 30000) {
    this.threshold = threshold;
    this.timeout = timeout;
    this.monitor = monitor;
    this.failures = 0;
    this.state = 'CLOSED';
    this.nextAttempt = Date.now();
  }
  
  async call(fn) {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttempt) {
        throw new Error('Circuit breaker is OPEN');
      }
      this.state = 'HALF_OPEN';
    }
    
    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }
  
  onSuccess() {
    this.failures = 0;
    this.state = 'CLOSED';
  }
  
  onFailure() {
    this.failures++;
    if (this.failures >= this.threshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.timeout;
    }
  }
}

// Usage
const breaker = new CircuitBreaker();

exports.handler = async (event) => {
  try {
    const result = await breaker.call(async () => {
      return await externalAPICall();
    });
    
    return {
      statusCode: 200,
      body: JSON.stringify(result)
    };
  } catch (error) {
    return {
      statusCode: 503,
      body: JSON.stringify({ error: 'Service temporarily unavailable' })
    };
  }
};
\\\`\\\`\\\`

## Performance Optimization

### Cold Start Mitigation

\\\`\\\`\\\`javascript
// Provisioned concurrency with warm-up
const isWarmStart = global.isWarm;
global.isWarm = true;

exports.handler = async (event) => {
  if (!isWarmStart) {
    // Initialize expensive resources once
    await initializeDatabase();
    await preloadCache();
  }
  
  // Warm-up ping
  if (event.source === 'aws.events' && event['detail-type'] === 'Warm Up') {
    return { statusCode: 200, body: 'Warmed up' };
  }
  
  // Normal request processing
  return processRequest(event);
};
\\\`\\\`\\\`

### Connection Pooling

\\\`\\\`\\\`javascript
// Database connection pooling
const mysql = require('mysql2/promise');

// Create pool outside handler (reused across invocations)
const pool = mysql.createPool({
  host: process.env.DB_HOST,
  user: process.env.DB_USER,
  password: process.env.DB_PASSWORD,
  database: process.env.DB_NAME,
  connectionLimit: 10,
  acquireTimeout: 60000,
  timeout: 60000
});

exports.handler = async (event) => {
  let connection;
  
  try {
    connection = await pool.getConnection();
    const [rows] = await connection.execute('SELECT * FROM users WHERE id = ?', [event.pathParameters.id]);
    
    return {
      statusCode: 200,
      body: JSON.stringify(rows[0])
    };
  } finally {
    if (connection) connection.release();
  }
};
\\\`\\\`\\\`

This comprehensive module covers all aspects of monitoring, deployment, and operational excellence for serverless microservices, providing practical examples and production-ready patterns.`
      }
    ]
  }
];